/**
 * Dopunski rječnik portala.
 *
 * `@st/i18n` je izvor istine za sve zajedničke nizove (nav, common, empty, error, auth,
 * pricing, checkout, portal). Ovdje su samo nizovi koje treba isključivo portal.
 * Rječnici se spajaju u `I18nProvider`; portal nikad ne prepisuje ključ iz `@st/i18n`.
 */
import type { Locale } from '@st/i18n';

const hr = {
  shell: {
    trainer: 'Trener',
    admin: 'Admin',
    clients: 'Klijenti',
    programs: 'Programi',
    exercises: 'Vježbe',
    users: 'Korisnici',
    products: 'Proizvodi',
    content: 'Sadržaj',
    payments: 'Plaćanja',
    analytics: 'Analitika',
    submissions: 'Upiti',
    checkIn: 'Check-in',
    habits: 'Navike',
    education: 'Edukacija',
    discountCodes: 'Kodovi',
    calendar: 'Kalendar',
    website: 'Web stranica',
    logout: 'Odjava',
    language: 'Jezik',
    menu: 'Izbornik',
    skipToContent: 'Preskoči na sadržaj',
  },
  dash: {
    greeting: 'Bok, {name}',
    greetingAnon: 'Bok',
    today: 'Danas',
    consistency: 'Dosljednost',
    consistencyOf: '{done}/{planned} treninga',
    consistencyHelp: 'Ovaj tjedan',
    caloriesToday: 'Kalorije danas',
    kcalOf: '{eaten} / {goal} kcal',
    lastMessage: 'Zadnja poruka trenera',
    openMessages: 'Otvori poruke',
    exercisesCount: '{n} vježbi',
    minutes: '{n} min',
    volumeWeek: 'Volumen ovaj tjedan',
    weight: 'Težina',
    weightGoal: 'Cilj {n} kg',
    streakUnit: 'dana zaredom',
  },
  training: {
    title: 'Trening',
    plan: 'Plan',
    history: 'Povijest',
    sets: 'Setovi',
    reps: 'Ponavljanja',
    weightKg: 'Kilaža (kg)',
    addedWeightKg: 'Dodatni uteg (kg)',
    addWeight: '+ uteg',
    durationSeconds: 'Trajanje (s)',
    rest: 'Odmor',
    restTimer: 'Tajmer odmora',
    restDone: 'Odmor je gotov.',
    finishNeedsSet: 'Označi barem jedan set kvačicom pa se trening može zaključiti.',
    leaveTitle: 'Trening još nije spremljen',
    leaveQuestion: 'Ako sada odeš s ekrana, uneseni setovi se gube. Želiš li ipak izaći?',
    leaveConfirm: 'Izađi bez spremanja',
    leaveStay: 'Ostani na treningu',
    start: 'Započni',
    pause: 'Pauza',
    resume: 'Nastavi',
    skip: 'Preskoči',
    addSet: 'Dodaj set',
    setN: 'Set {n}',
    markDone: 'Označi set',
    finish: 'Završi trening',
    finished: 'Trening završen',
    totalVolume: 'Ukupni volumen',
    duration: 'Trajanje',
    newRecords: 'Novi rekordi',
    noRecords: 'Bez novih rekorda — ali svaki set se broji.',
    backToDashboard: 'Natrag na početnu',
    tempo: 'Tempo',
    trainerNote: 'Bilješka trenera',
    invalidReps: 'Ponavljanja moraju biti cijeli broj od 0 do 1000.',
    invalidWeight: 'Kilaža mora biti između 0 i 1000 kg.',
    invalidDuration: 'Trajanje mora biti između 0 i 28800 sekundi.',
    prWeight: 'najveća kilaža',
    prVolume: 'najveći volumen seta',
    prReps: 'najviše ponavljanja',
  },
  measureGuide: {
    title: 'Kako se mjeri',
    help: 'Za svaku točku upiši rečenicu i, ako imaš, snimku. Klijent to vidi kad u aplikaciji dodirne „Kako se mjeri" uz to polje. Točka bez snimke nije greška — tada se ponudi opća snimka cijelog mjerenja, a ako ni nje nema, samo tekst.',
    textHr: 'Uputa (hrvatski)',
    textEn: 'Uputa (engleski)',
    hasVideo: 'Ima snimku',
    noVideo: 'Bez snimke',
    upload: 'Postavi snimku',
    replace: 'Zamijeni snimku',
    remove: 'Makni snimku',
    uploading: 'Šaljem snimku…',
    uploaded: 'Snimka je prenesena. Spremi da je klijenti vide.',
    saved: 'Uputa je spremljena.',
    preview: 'Pregled snimke: {point}',
    tooLarge: 'Snimka je prevelika (najviše {max} MB). Snimi kraći isječak ili ga smanji.',
    wrongType: 'Dopušteni su samo MP4 i WebM.',
  },
  transformacije: {
    title: 'Transformacije',
    intro:
      'Parovi „prije / poslije" koji idu na javni sajt. Objavljuje se tek kad postoje obje fotografije i kad ti to izričito potvrdiš — novi unos nikad ne izađe sam od sebe.',
    consent:
      'Objavljuj samo fotografije osoba koje su potpisale pristanak. Ako netko pristane na sliku ali ne i na ime, dogovori to prije objave.',
    nova: 'Nova transformacija',
    ime: 'Ime',
    imeHint: 'Kako će stajati ispod fotografije na sajtu.',
    godine: 'Godine',
    mjera: 'Rezultat',
    mjeraHint: 'Minus je gubitak, plus dobitak. Bez rezultata kartica na sajtu nema što tvrditi.',
    jedinicaKg: 'kilograma',
    jedinicaCm: 'centimetara struka',
    tekstHr: 'Izjava (hrvatski)',
    tekstEn: 'Izjava (engleski)',
    prije: 'Prije',
    poslije: 'Poslije',
    postavi: 'Postavi fotografiju',
    zamijeni: 'Zamijeni',
    saljem: 'Šaljem fotografiju…',
    objavljeno: 'Objavljeno',
    skica: 'Skica',
    objavi: 'Objavi',
    sakrij: 'Sakrij sa sajta',
    obrisi: 'Obriši',
    obrisiPotvrda: 'Obrisati ovu transformaciju? Fotografije ostaju u pohrani, zapis ne.',
    spremi: 'Spremi',
    spremljeno: 'Spremljeno.',
    prazno: 'Još nema nijedne transformacije.',
    trebaObje:
      'Za objavu trebaju obje fotografije — jedna slika pokraj prazne nije usporedba.',
    prevelika: 'Fotografija je prevelika (najviše {max} MB).',
    krivTip: 'Dopušteni su JPEG, PNG i WebP.',
    uzKod:
      'Prvih 58 transformacija stoji uz kod sajta i ne pojavljuje se u ovom popisu. Sve što uneseš ovdje dolazi uz njih.',
  },
  mealTemplates: {
    title: 'Predlošci obroka',
    intro: 'Jelovnik koji složiš jednom, a klijenti biraju jednim dodirom. Rješava ono što ih najviše koči: ne moraju sami računati kalorije i makronutrijente.',
    new: 'Novi predložak',
    empty: 'Još nema nijednog predloška.',
    name: 'Naziv',
    nameHint: 'Onako kako će ga klijent vidjeti — „Zobene s bananom", „Ručak: piletina i riža".',
    nameRequired: 'Napiši naziv predloška.',
    slot: 'Kada',
    slotHint: 'Ostavi prazno ako jelo nije vezano uz doba dana.',
    anySlot: 'Bilo kada',
    note: 'Napomena',
    noteHint: 'Količina, zamjena, što uz to. Klijent ovo vidi uz predložak.',
    newTitle: 'Novi predložak obroka',
    editTitle: 'Uredi predložak',
    saved: 'Predložak je spremljen.',
    deleted: 'Predložak je obrisan.',
    deleteTitle: 'Brisanje predloška',
    deleteQuestion: 'Obrisati „{name}"? Već upisani obroci ostaju netaknuti — predložak se pri odabiru prepisuje u obrok.',
    pickTitle: 'Trenerov jelovnik',
    pickHelp: 'Dodirni i brojke se popune same. Možeš ih poslije ispraviti.',
    pick: 'Upiši: {meal}',
    fromMeal: 'Spremi kao predložak',
    fromMealTitle: 'Novi predložak iz obroka',
  },
  nutrition: {
    photo: 'Fotografija obroka',
    photoHint: 'Slikaj tanjur — treneru je puno jasnije kako jedeš nego iz opisa. Neobavezno.',
    photoPick: 'Odaberi ili slikaj',
    photoPreview: 'Pregled odabrane fotografije',
    photoSavedMealOnly: 'Obrok je spremljen, ali fotografija nije prošla.',
    photoRetry: 'Pošalji fotografiju ponovno',
    photoSkip: 'Nastavi bez fotografije',
    photoTypeInvalid: 'Podržani su JPEG, PNG, WebP i HEIC.',
    photoTooLarge: 'Fotografija je prevelika. Najviše {max} MB.',
    title: 'Prehrana',
    kcal: 'Kalorije',
    protein: 'Proteini',
    carbs: 'Ugljikohidrati',
    fat: 'Masti',
    meals: 'Obroci',
    addMeal: 'Dodaj obrok',
    grams: '{n} g',
    prevDay: 'Prethodni dan',
    nextDay: 'Sljedeći dan',
    water: 'Voda',
    trainerComment: 'Trener',
    mealPhoto: 'Fotografija obroka',
    photoOpen: 'Povećaj fotografiju obroka',
    formLead: 'Dovoljno je napisati što je bilo u obroku. Sve ostalo je neobavezno.',
    stepWhat: 'Što je bilo u obroku?',
    stepHowMuch: 'Koliko',
    stepPhoto: 'Slika',
    required: 'obavezno',
    optional: 'neobavezno',
    quickTitle: 'Brže upiši',
    howMuchHint: 'Ne znaš brojke? Preskoči ih. Trener obrok vidi i bez toga.',
    foodsTitle: 'Namirnice',
    foodsSearch: 'Traži namirnicu',
    foodsSearchHint: 'piletina, riža, banana…',
    foodsGrams: 'Koliko grama',
    foodsAdd: 'Dodaj u obrok',
    foodsPer100: '{kcal} kcal / 100 g',
    foodsResult: 'Dodaje {kcal} kcal i {protein} g proteina.',
    slotBreakfast: 'Doručak',
    slotLunch: 'Ručak',
    slotDinner: 'Večera',
    slotSnack: 'Međuobrok',
    slot: 'Termin',
    description: 'Opis obroka',
    descriptionHint: 'Napiši što je bilo u obroku i koliko otprilike — trener na tome gradi savjet.',
    descriptionRequired: 'Napiši što je bilo u obroku — to je jedino što treba.',
    kcalRange: 'Kalorije moraju biti cijeli broj između 0 i {max}.',
    macroRange: 'Vrijednost mora biti između 0 i {max} g.',
    mealSaved: 'Obrok je spremljen.',
    mealDeleted: 'Obrok je obrisan.',
    deleteMeal: 'Brisanje obroka',
    deleteMealQuestion: 'Obrisat ćemo obrok „{meal}". Ovo se ne može poništiti.',
    deleteMealOf: 'Obriši obrok: {meal}',
    history: 'Povijest prehrane',
    mealsCount: '{n} obroka',
  },
  progress: {
    title: 'Napredak',
    weightChart: 'Težina kroz vrijeme',
    measurements: 'Mjere',
    photos: 'Fotografije',
    addMeasurement: 'Dodaj mjeru',
    nothingToSave: 'Upiši barem jednu vrijednost prije spremanja.',
    addPhoto: 'Dodaj fotografiju',
    before: 'Prije',
    now: 'Sada',
    waist: 'Struk',
    chest: 'Prsa',
    hips: 'Bokovi',
    arm: 'Nadlaktica',
    thigh: 'Bedro',
    bodyFat: 'Postotak masti',
    delta: 'Promjena',
    charts: 'Grafovi',
    noChartData: 'Još nema dovoljno mjerenja za graf.',
    guide: 'Kako se pravilno izmjeriti',
    guideHelp: 'Mjeri se ujutro, natašte, uvijek na istom mjestu.',
    guideVideo: 'Video uputa za mjerenje',
    guideNoVideo: 'Video uputa još nije snimljena — dok ne stigne, vrijede pisani koraci.',
    uploadPhoto: 'Pošalji fotografiju',
    photoFile: 'Fotografija',
    photoLimits: 'JPEG, PNG, WebP ili HEIC, najviše {max} MB.',
    photoTypeInvalid: 'Ovaj tip datoteke nije podržan. Odaberi JPEG, PNG, WebP ili HEIC.',
    photoTooLarge: 'Fotografija je veća od {max} MB. Odaberi manju ili je prvo smanji.',
    photoRequired: 'Odaberi fotografiju prije slanja.',
    photoPreview: 'Pregled odabrane fotografije',
    photoNote: 'Bilješka uz fotografiju',
    photoUploaded: 'Fotografija je spremljena.',
    photoUnavailable: 'Fotografija trenutačno nije dostupna.',
    photoPrivacy: 'Fotografije napretka su privatne. Trener ih vidi samo ako mu ti izričito odobriš pristup.',
    takenOn: 'Datum snimanja',
    pose: 'Poza',
    poseFront: 'Sprijeda',
    poseSide: 'Sa strane',
    poseBack: 'Straga',
    poseOther: 'Ostalo',
    stageGrant: 'Pripremamo prijenos…',
    stageUpload: 'Šaljemo fotografiju…',
    stageConfirm: 'Spremamo zapis…',
    retryFromConfirm: 'Fotografija je već prenesena — ponovni pokušaj samo dovršava spremanje.',
  },
  messages: {
    title: 'Poruke',
    placeholder: 'Napiši poruku…',
    send: 'Pošalji',
  },
  profile: {
    title: 'Profil',
    personal: 'Osobni podaci',
    preferences: 'Postavke',
    units: 'Mjerne jedinice',
    metric: 'Metričke (kg, cm)',
    imperial: 'Imperijalne (lb, in)',
    timezone: 'Vremenska zona',
    changePassword: 'Promijeni lozinku',
    saved: 'Spremljeno.',
  },
  install: {
    title: 'Aplikacija na mobitelu',
    body: 'Dodaj Soul Training na početni zaslon i otvara se kao aplikacija — bez adresne trake, s ikonom uz ostale aplikacije. Do izlaska na Trgovinu Play radi jednako.',
    action: 'Dodaj na početni zaslon',
    installed: 'Aplikacija je već dodana na ovaj uređaj.',
    iosTitle: 'Na iPhoneu i iPadu',
    iosSteps: 'Otvori Safari, dodirni ikonu dijeljenja i odaberi „Add to Home Screen”.',
    manualTitle: 'Na ovom pregledniku',
    manualSteps: 'Otvori izbornik preglednika i odaberi „Instaliraj aplikaciju” ili „Dodaj na početni zaslon”.',
    done: 'Aplikacija je dodana na početni zaslon.',
  },
  webPush: {
    title: 'Obavijesti u pregledniku',
    body: 'Dok je preglednik pokrenut, obavijest stiže i kad portal nije otvoren — poruka od klijenta, novi unos prehrane, zakazan termin.',
    switch: 'Obavijesti na ovom uređaju',
    on: 'Uključene. Stižu i kad portal nije otvoren.',
    off: 'Isključene. Obavijest ćeš vidjeti samo u zvoncu, dok je portal otvoren.',
    test: 'Pošalji probnu',
    blocked: 'Preglednik je blokirao obavijesti za ovu stranicu.',
    blockedHelp: 'Prekidač ovdje to ne može vratiti — otvori postavke stranice (ikona lijevo od adrese), dopusti obavijesti i osvježi stranicu.',
    unsupported: 'Ovaj preglednik ne podržava obavijesti.',
    notReady: 'Obavijesti rade na objavljenom portalu, ne u lokalnom razvoju.',
    notConfigured: 'Obavijesti u pregledniku još nisu uključene na poslužitelju.',
    enabled: 'Obavijesti su uključene.',
    moved: 'Obavijesti na ovom pregledniku sada idu tebi. Račun koji ih je ovdje imao prije više ih ne dobiva — svaki preglednik ima samo jednu pretplatu.',
    disabled: 'Obavijesti su isključene.',
    failed: 'Uključivanje nije uspjelo. Pokušaj ponovno.',
  },
  subscription: {
    title: 'Pretplata',
    currentPlan: 'Trenutni plan',
    status: 'Status',
    nextPayment: 'Sljedeće plaćanje',
    noNextPayment: 'Nema sljedećeg plaćanja',
    managePortal: 'Upravljaj pretplatom',
    managePortalHelp: 'Kartica, računi i otkazivanje idu preko Stripe Customer Portala.',
    accessUntil: 'Pristup do {date}',
    statusActive: 'Aktivna',
    statusPastDue: 'Neuspjelo plaćanje',
    statusCanceled: 'Otkazana',
    statusExpired: 'Istekla',
    invoices: 'Računi',
    choosePlan: 'Odaberi paket',
    plans: 'Paketi',
    plansHelp: 'Kupnjom se otključava cijeli program, bez ograničenja broja treninga.',
    buy: 'Kupi pristup',
    checkoutFailed: 'Kupnja se nije mogla pokrenuti.',
  },
  onboarding: {
    title: 'Upoznajmo se',
    step: 'Korak {n} od {total}',
    finish: 'Završi',
    done: 'Hvala! Tvoj trener sada ima sve što treba.',
    goal: 'Koji ti je glavni cilj?',
    activity: 'Koliko si aktivna?',
    body: 'Osnovne mjere',
    height: 'Visina (cm)',
    startWeight: 'Trenutna težina (kg)',
    experience: 'Iskustvo s treningom',
    equipment: 'Oprema koju imaš',
    notes: 'Ozljede, ograničenja, sve što bih trebao znati',
    goalLoss: 'Mršavljenje',
    goalTone: 'Tonifikacija',
    goalStrength: 'Snaga i masa',
    goalHealth: 'Energija i zdravlje',
    actSedentary: 'Sedentarna',
    actModerate: 'Umjerena',
    actActive: 'Aktivna',
    actVeryActive: 'Vrlo aktivna',
    expNone: 'Bez iskustva',
    expSome: 'Povremeno treniram',
    expRegular: 'Redovito treniram',
    eqNone: 'Bez opreme',
    eqHome: 'Kućna oprema',
    eqGym: 'Teretana',
  },
  wizard: {
    title: 'Upoznajmo se',
    stepOf: 'Korak {n} od {total}',
    basics: 'Osnovni podaci',
    goal: 'Tvoj cilj',
    program: 'Tvoj program',
    enterHint: 'Pritisni Enter za nastavak',
    firstName: 'Kako ti je ime?',
    firstNameLabel: 'Ime',
    lastName: 'A prezime?',
    lastNameLabel: 'Prezime',
    birthDate: 'Kada si rođen/a?',
    birthDateLabel: 'Datum rođenja',
    birthDateHint: 'Godine su nam potrebne za procjenu opterećenja.',
    gender: 'Spol',
    genderLabel: 'Spol',
    genderFemale: 'Žensko',
    genderMale: 'Muško',
    genderOther: 'Ostalo',
    genderUndisclosed: 'Ne želim reći',
    heightCm: 'Koliko si visok/a?',
    heightLabel: 'Visina',
    weightKg: 'Koliko sada imaš kilograma?',
    weightLabel: 'Težina',
    weightHint: 'Ovo je ujedno tvoja prva mjera — graf težine kreće od nje.',
    required: 'Ovo polje je obavezno.',
    invalidHeight: 'Visina mora biti između 100 i 250 cm.',
    invalidWeight: 'Težina mora biti između 30 i 300 kg.',
    invalidDate: 'Odaberi datum rođenja.',
    goalQuestion: 'Što želiš postići?',
    goalHelp: 'Odaberi jedno. Cilj se kasnije može promijeniti.',
    targetWeight: 'Željena kilaža',
    targetWeightHint: 'Do koje kilaže želiš doći?',
    targetWeightRequired: 'Za cilj mršavljenja upiši željenu kilažu.',
    programQuestion: 'Odaberi program za početak',
    programHelp: 'Kreni odmah — prvi treninzi su besplatni i kartica ti ne treba.',
    trialBadgeOne: 'prvi trening besplatno',
    trialBadgeFew: 'prva {n} treninga besplatno',
    trialBadgeMany: 'prvih {n} treninga besplatno',
    chooseProgram: 'Odaberi ovaj program',
    noPrograms: 'Programi trenutno nisu dostupni. Javi se treneru.',
    done: 'Sve je spremno. Krenimo.',
  },
  trial: {
    title: 'Probni pristup',
    remainingOne: 'još {n} besplatan trening',
    remainingFew: 'još {n} besplatna treninga',
    remainingMany: 'još {n} besplatnih treninga',
    bannerHelp: 'Nakon toga se ostatak programa otključava kupnjom.',
    used: 'Iskorišteno {used} od {total}',
    lockedTitle: 'Ovaj trening se otključava kupnjom',
    lockedHelp: 'Prvi treninzi programa su besplatni. Odaberi paket i program se otvara do kraja.',
    sectionTitle: 'Ovo se otključava paketom',
    sectionHelp:
      'Prehrana, mjere, poruke i tjedni check-in dio su vođenja s trenerom. Odaberi paket i sve se otvara.',
    lockedBadge: 'Zaključano',
    unlock: 'Otključaj kupnjom',
    lockedFrom: 'Zaključano od treninga: {name}',
    locked: 'Zaključan trening',
  },
  trainer: {
    title: 'Trener',
    clients: 'Klijenti',
    clientProfile: 'Profil klijenta',
    tabOverview: 'Pregled',
    tabWorkouts: 'Treninzi',
    flagMissed: '{n} propuštenih',
    flagNoMeasurements: 'Bez mjera',
    flagUnread: 'Nepročitana poruka',
    lastActive: 'Zadnja aktivnost',
    consistency: 'Dosljednost',
    assignedProgram: 'Dodijeljen program',
    weeks: '{n} tjedana',
    newProgram: 'Novi program',
    newExercise: 'Nova vježba',
    muscleGroup: 'Mišićna skupina',
    equipment: 'Oprema',
    checkIns: 'Check-inovi',
    messages: 'Poruke',
    all: 'Sve',
    clientSummaries: 'Tjedni sažetci klijenta',
    clientSummariesHelp: 'Isti sažetak koji klijent dobije ponedjeljkom.',
    clientSummariesEmpty:
      'Klijent još nema nijedan tjedni sažetak. Prvi stiže u ponedjeljak nakon tjedna treninga.',
    clientNutrition: 'Prehrana',
    clientNutritionHelp: 'Obroci koje klijent upisuje i slika. Ostavi palac ili komentar — klijent ga vidi uz obrok.',
    clientNutritionEmpty: 'Za taj dan nema upisanih obroka.',
    nutritionDayPick: 'Dan s upisom',
    mealLike: 'Palac',
    mealLikeOf: 'Označi da je „{meal}" u redu',
    mealUnlikeOf: 'Makni oznaku s „{meal}"',
    mealComment: 'Komentiraj',
    mealCommentEdit: 'Uredi komentar',
    mealCommentLabel: 'Komentar uz „{meal}"',
    clientAchievements: 'Postignuća klijenta',
    clientAchievementsEmpty: 'Klijent još nije otključao nijedno postignuće.',
  },
  admin: {
    title: 'Admin',
    users: 'Korisnici',
    products: 'Proizvodi i cijene',
    content: 'Sadržaj',
    payments: 'Plaćanja',
    analytics: 'Analitika',
    role: 'Uloga',
    createdAt: 'Registriran',
    lastSeen: 'Zadnji put',
    deleted: 'Obrisan',
    access: 'Pristup',
    accessActive: 'Aktivan',
    accessNone: 'Nema',
    grantAccess: 'Odobri pristup',
    revokeAccess: 'Oduzmi pristup',
    grantTitle: 'Ručno odobrenje pristupa',
    grantHelp:
      'Za uplatu izvan Stripea — na račun ili na ruke. Klijent time postaje trenerov i aplikacija mu se otključava.',
    grantProduct: 'Paket',
    grantUntil: 'Vrijedi do',
    grantUntilHelp: 'Prazno znači bez roka.',
    grantNote: 'Bilješka',
    grantNoteHelp: 'Zapisuje se trajno. Npr. „Uplata na račun 4.9., 150 €".',
    revokeTitle: 'Oduzimanje pristupa',
    revokeHelp: 'Gasi sva ručno odobrena prava ovog klijenta. Stripe pretplate se ovim ne diraju.',
    price: 'Cijena',
    interval: 'Interval',
    compareAt: 'Precrtano',
    active: 'Aktivan',
    inactive: 'Neaktivan',
    amount: 'Iznos',
    method: 'Način',
    refund: 'Povrat',
    paid: 'Plaćeno',
    failed: 'Neuspjelo',
    refunded: 'Vraćeno',
    pending: 'U obradi',
    submissions: 'Upiti',
    conversion: 'Konverzija',
    activeUsers: 'Aktivni korisnici (30 d)',
    churn: 'Odlazak klijenata',
    churnHint: 'Udio otkazanih pretplata u zadnjih 30 dana. Manje je bolje.',
  },
  auth: {
    loginTitle: 'Dobro došli natrag',
    loginSubtitle: 'Prijavi se u svoj portal.',
    registerTitle: 'Otvori račun',
    registerSubtitle: 'Račun postoji prije plaćanja — tako ništa ne izgubiš.',
    forgotTitle: 'Zaboravljena lozinka',
    forgotSubtitle: 'Pošaljemo ti link za postavljanje nove lozinke.',
    forgotSubmit: 'Pošalji link',
    forgotSent: 'Ako račun postoji, link je poslan na tu adresu.',
    resetTitle: 'Nova lozinka',
    resetSubmit: 'Postavi lozinku',
    resetDone: 'Lozinka je promijenjena. Prijavi se.',
    verifyTitle: 'Potvrda email adrese',
    verifyPending: 'Provjeravamo link…',
    verifyDone: 'Email je potvrđen. Možeš se prijaviti.',
    verifyFailed: 'Link nije važeći ili je istekao.',
    verifyResend: 'Pošalji novi link',
    noAccount: 'Nemaš račun?',
    hasAccount: 'Već imaš račun?',
    acceptTerms: 'Prihvaćam uvjete korištenja i pravila privatnosti.',
    termsRequired: 'Bez prihvaćanja uvjeta ne možemo otvoriti račun.',
    passwordHint: 'Najmanje 10 znakova.',
    passwordMismatch: 'Lozinke se ne poklapaju.',
    emailTaken: 'Račun s tom adresom već postoji.',
    backToLogin: 'Natrag na prijavu',
    missingToken: 'Link ne sadrži token.',
  },
  empty: {
    noWorkoutsPlanned: 'Nema propisanih treninga.',
    noExercises: 'Biblioteka vježbi je prazna.',
    noProducts: 'Nema proizvoda.',
    noPayments: 'Nema zabilježenih plaćanja.',
    noUsers: 'Nema korisnika.',
    noInvoices: 'Nema računa.',
    noSubscription: 'Nemaš aktivnu pretplatu.',
    noSubmissions: 'Nema upita. Kad netko pošalje kontakt formu ili zatraži procjenu, pojavit će se ovdje.',
    addFirstMeasurement: 'Dodaj mjeru',
    addFirstMeal: 'Dodaj obrok',
    addFirstPhoto: 'Dodaj fotografiju',
    browsePlans: 'Pogledaj pakete',
    /*
      Osnovni rječnik nudi „Dnevnik prehrane je prazan. Dodaj prvi obrok." za
      cijeli dnevnik; ekran prehrane gleda JEDAN dan, pa poruka mora reći koji.
      Uz nju sada stoji i prava akcija — ugovor je dobio `nutrition.addMeal`.
    */
    noMealsPortal: 'Za ovaj dan nema upisanih obroka.',
    noHistory: 'Povijest prehrane je prazna. Prvi upisani obrok pojavit će se ovdje.',
    onePhotoOnly: 'Imaš jednu fotografiju napretka. Usporedba se pojavi kad ih bude barem dvije.',
    noProgramChosen: 'Još nemaš odabran program. Odaberi ga i prvi treninzi su odmah otvoreni.',
    chooseProgram: 'Odaberi program',
  },
  error: {
    rateLimited: 'Previše pokušaja.',
    rateLimitedHelp: 'Pričekaj minutu pa pokušaj ponovno.',
    validation: 'Provjeri unesene podatke.',
    validationHelp: 'Neka polja nisu ispravno popunjena.',
    conflict: 'Podaci su se u međuvremenu promijenili.',
    conflictHelp: 'Osvježi stranicu i pokušaj ponovno.',
  },
  builder: {
    title: 'Graditelj programa',
    backToPrograms: 'Svi programi',
    openBuilder: 'Otvori',
    newProgramTitle: 'Novi program',
    editProgram: 'Uredi program',
    deleteProgram: 'Obriši program',
    deleteProgramConfirm: 'Obrisati program „{name}"? Klijenti kojima je već dodijeljen zadržavaju svoj plan.',
    programName: 'Naziv programa',
    programNameEn: 'Naziv na engleskom',
    programDescription: 'Opis',
    programLevel: 'Razina (1–5)',
    isTemplate: 'Predložak',
    isTemplateHelp: 'Predložak se dodjeljuje većem broju klijenata.',
    nameRequired: 'Naziv programa je obavezan.',
    weeksShort: '{n} tj.',
    created: 'Program je napravljen.',
    deleted: 'Program je obrisan.',
    saved: 'Spremljeno.',
    mon: 'Pon',
    tue: 'Uto',
    wed: 'Sri',
    thu: 'Čet',
    fri: 'Pet',
    sat: 'Sub',
    sun: 'Ned',
    week: 'Tjedan {n}',
    addWeek: 'Dodaj tjedan',
    duplicateWeek: 'Dupliciraj tjedan',
    deleteWeek: 'Obriši tjedan',
    deleteWeekConfirm: 'Obrisati {week} sa svim treninzima? Ostali tjedni se preslažu.',
    weekDuplicated: 'Tjedan je dupliciran.',
    emptyProgram: 'Program još nema nijedan tjedan. Dodaj prvi i rasporedi treninge.',
    addWorkout: 'Dodaj trening',
    newWorkout: 'Novi trening',
    editWorkout: 'Uredi trening',
    duplicateWorkout: 'Dupliciraj trening',
    deleteWorkout: 'Obriši trening',
    deleteWorkoutConfirm: 'Obrisati trening „{name}" i sve njegove vježbe?',
    workoutName: 'Naziv treninga',
    workoutType: 'Tip treninga',
    estimatedMinutes: 'Procijenjeno trajanje (min)',
    workoutNote: 'Bilješka uz trening',
    day: 'Dan',
    typeStrength: 'Snaga',
    typeCardio: 'Kondicija',
    typeMobility: 'Mobilnost',
    typeRest: 'Odmor',
    typeMixed: 'Kombinirano',
    exercises: 'Vježbe',
    exerciseCount: '{n} vježbi',
    noExercises: 'Trening još nema vježbi.',
    addExercise: 'Dodaj vježbu',
    searchExercise: 'Traži vježbu po nazivu',
    allCategories: 'Sve kategorije',
    noSearchResults: 'Nijedna vježba ne odgovara pretrazi.',
    moveUp: 'Pomakni gore',
    moveDown: 'Pomakni dolje',
    removeExercise: 'Ukloni vježbu',
    reorderHint: 'Povuci vježbu ili koristi strelice za promjenu redoslijeda.',
    reorderFailed: 'Redoslijed nije spremljen.',
    setsLabel: 'Setovi',
    repsLabel: 'Ponavljanja',
    repsHint: 'Slobodan tekst: 8–12, do otkaza…',
    restLabel: 'Odmor (s)',
    tempoLabel: 'Tempo',
    weightHint: 'Preporučena kilaža (kg)',
    noteLabel: 'Bilješka za klijenta',
    prescribedSets: 'Propisani setovi',
    prescribedSetsHelp: 'Piramida ili drop set, set po set. Prazno znači da vrijedi propis iznad.',
    addPrescribedSet: 'Dodaj propisani set',
    removePrescribedSet: 'Ukloni propisani set',
    setWeight: 'Kilaža (kg)',
    setNote: 'Bilješka seta',
    saveExercise: 'Spremi vježbu',
    exerciseSaved: 'Vježba je spremljena.',
    dayTaken: 'Taj dan već ima trening.',
    weekFull: 'Tjedan je pun — nema slobodnog dana.',
    assign: 'Dodijeli klijentu',
    assignTitle: 'Dodjela programa',
    assignClient: 'Klijent',
    chooseClient: 'Odaberi klijenta',
    assignStartDate: 'Datum početka',
    assignReplace: 'Zamijeni aktivni program klijenta',
    assignReplaceHelp: 'Prethodna dodjela se zatvara, povijest ostaje sačuvana.',
    assignSubmit: 'Dodijeli program',
    assignDone: 'Program je dodijeljen klijentu.',
    clientRequired: 'Odaberi klijenta.',
    startDateRequired: 'Odaberi datum početka.',
  },
  exerciseLib: {
    newTitle: 'Nova vježba',
    editTitle: 'Uredi vježbu',
    slug: 'Slug',
    slugHint: 'Samo mala slova, brojke i crtice.',
    slugInvalid: 'Slug smije sadržavati samo mala slova, brojke i crtice.',
    slugRequired: 'Slug je obavezan.',
    nameHr: 'Naziv (hrvatski)',
    nameEn: 'Naziv (engleski)',
    nameRequired: 'Oba naziva su obavezna.',
    category: 'Kategorija',
    muscleGroups: 'Mišićne skupine',
    muscleGroupsHint: 'Odvoji zarezom.',
    equipment: 'Oprema',
    loadType: 'Način mjerenja',
    loadTypeHint: 'Određuje koja polja klijent vidi na treningu.',
    loadWeighted: 'Ponavljanja i kilaža',
    loadBodyweight: 'Vlastita težina (samo ponavljanja)',
    loadTimed: 'Izdržaj (trajanje)',
    difficulty: 'Težina',
    difficultyBeginner: 'Početna',
    difficultyIntermediate: 'Srednja',
    difficultyAdvanced: 'Napredna',
    instructions: 'Upute',
    globalBadge: 'Globalna',
    edit: 'Uredi',
    catChest: 'Prsa',
    catBack: 'Leđa',
    catShoulders: 'Ramena',
    catArms: 'Ruke',
    catLegs: 'Noge',
    catGlutes: 'Gluteus',
    catCore: 'Trup',
    catCardio: 'Kondicija',
    catMobility: 'Mobilnost',
    catFullBody: 'Cijelo tijelo',
  },
  product: {
    newTitle: 'Novi proizvod',
    editTitle: 'Uredi proizvod',
    edit: 'Uredi',
    slug: 'Slug',
    slugRequired: 'Slug je obavezan.',
    nameHr: 'Naziv (hrvatski)',
    nameEn: 'Naziv (engleski)',
    nameHrRequired: 'Hrvatski naziv je obavezan.',
    nameEnRequired: 'Engleski naziv je obavezan.',
    descriptionHr: 'Opis (hrvatski)',
    descriptionEn: 'Opis (engleski)',
    featuresHr: 'Značajke (hrvatski)',
    featuresEn: 'Značajke (engleski)',
    featuresHelp: 'Jedna značajka po retku.',
    accessDurationDays: 'Trajanje pristupa (dana)',
    accessDurationHelp: 'Prazno znači da pristup traje dok traje pretplata.',
    accessDurationValue: 'Pristup {n} dana',
    deliveryMode: 'Način isporuke',
    modeOnline: 'Online',
    modeGym: 'Uživo',
    modeHybrid: 'Kombinirano',
    highlighted: 'Istaknut na cjeniku',
    activeLabel: 'Aktivan u prodaji',
    sortOrder: 'Redoslijed',
    noPrices: 'Paket još nema cijenu, pa se ne može kupiti.',
    saved: 'Proizvod je spremljen.',
    trialWorkouts: 'Besplatnih treninga',
    trialWorkoutsHelp: 'Koliko treninga klijent odradi prije nego se traži plaćanje. 0 = nijedan.',
    program: 'Plan treninga',
    programHelp: 'Što klijent dobiva kupnjom. Bez plana paket nema program.',
    programNone: 'Bez plana',
    sortOrderHelp: 'Manji broj je bliže vrhu cjenika.',
  },
  price: {
    newTitle: 'Nova cijena',
    editTitle: 'Uredi cijenu',
    replaceTitle: 'Promijeni iznos',
    add: 'Dodaj cijenu',
    edit: 'Uredi',
    replace: 'Promijeni iznos',
    amount: 'Iznos',
    amountHelp: 'U eurima. Zapisuje se u centima.',
    currency: 'Valuta',
    compareAt: 'Precrtana cijena',
    compareAtHelp: 'Prikazuje se prekriženo pored iznosa. Ne utječe na naplatu.',
    interval: 'Razdoblje naplate',
    intervalCount: 'Svakih',
    intervalCountHelp: 'Kvartalna cijena je mjesec × 3.',
    labelHr: 'Oznaka (HR)',
    labelEn: 'Oznaka (EN)',
    labelHelp: 'Piše pored iznosa na cjeniku, npr. „kvartalno" ili „za 3 mjeseca".',
    activeLabel: 'Prikaži na cjeniku',
    amountRequired: 'Upiši iznos',
    saved: 'Cijena je spremljena.',
    replaced: 'Nova cijena je spremljena, stara je ugašena.',
    inStripe: 'U prodaji',
    notInStripe: 'Nije u prodaji',
    lockedHelp:
      'Ova cijena je već u Stripeu, pa se iznos ne može mijenjati — Stripe cijene su nepromjenjive. „Promijeni iznos" radi novu cijenu i gasi ovu.',
    replaceHelp:
      'Stara cijena se gasi i nestaje s cjenika, ali ostaje u bazi jer je vezana uz postojeće kupnje. Nova ide u prodaju nakon sinkronizacije.',
  },
  stripe: {
    title: 'Stripe',
    pendingNone: 'Sve cijene su u prodaji.',
    pending: 'Čeka sinkronizaciju: {products} paketa i {prices} cijena.',
    sync: 'Sinkroniziraj',
    synced: 'Sinkronizirano: {products} paketa, {prices} cijena.',
    help:
      'Sinkronizacija stvara paket i cijenu u Stripeu. Dok cijena nije ondje, na cjeniku se vidi ali se ne može kupiti.',
  },
  refund: {
    action: 'Povrat',
    title: 'Potvrda povrata',
    question: 'Vratiti {amount} kupcu {email}?',
    warning: 'Povrat se ne može poništiti. Stripe ga obrađuje odmah, a stanje potvrđuje webhook.',
    reason: 'Razlog (interna bilješka)',
    confirm: 'Pokreni povrat',
    done: 'Povrat je pokrenut.',
    onlySucceeded: 'Povrat je moguć samo za uspješna plaćanja.',
  },
  submissions: {
    title: 'Upiti',
    subtitle: 'Kontakt forma i besplatna procjena.',
    source: 'Izvor',
    sourceContactForm: 'Kontakt forma',
    sourceAssessment: 'Besplatna procjena',
    sourceOther: 'Ostalo',
    status: 'Status',
    statusNew: 'Novo',
    statusContacted: 'Kontaktirano',
    statusConverted: 'Pretvoreno',
    statusClosed: 'Zatvoreno',
    changeStatus: 'Promijeni status',
    statusSaved: 'Status je promijenjen.',
    message: 'Poruka',
    noMessage: 'Upit nema poruku.',
    assessment: 'Odgovori iz kalkulatora',
    received: 'Zaprimljeno',
    filterAll: 'Svi',
    internalNote: 'Interna bilješka',
    fieldGoal: 'Cilj',
    fieldActivity: 'Aktivnost',
    fieldHeightCm: 'Visina',
    fieldWeightKg: 'Težina',
    fieldAge: 'Dob',
    fieldRecommendation: 'Preporuka',
  },
  chat: {
    conversations: 'Razgovori',
    selectConversation: 'Odaberi razgovor s popisa.',
    backToList: 'Natrag na razgovore',
    unread: '{n} nepročitano',
    noConversations: 'Još nema razgovora.',
    noMessages: 'Još nema poruka u ovom razgovoru.',
    lastMessageNone: 'Bez poruka',
    attachmentPreview: 'Privitak',
    youPrefix: 'Ti: {text}',
    placeholder: 'Napiši poruku…',
    send: 'Pošalji',
    sending: 'Šalje se…',
    sendFailed: 'Poruka nije poslana.',
    live: 'Uživo',
    reconnecting: 'Ponovno spajanje…',
    polling: 'Veza uživo nije dostupna — osvježavanje svakih 15 s.',
    typing: '{name} tipka…',
    online: 'Na vezi',
    read: 'Pročitano',
    newConversation: 'Novi razgovor',
    startWith: 'Odaberi klijenta',
    started: 'Razgovor otvoren.',
  },
  notif: {
    title: 'Obavijesti',
    open: 'Otvori obavijesti',
    unreadCount: '{n} nepročitanih obavijesti',
    filterAll: 'Sve',
    filterUnread: 'Nove',
    markRead: 'Označi pročitanim',
    markUnread: 'Označi nepročitanim',
    markAllRead: 'Označi sve',
    empty: 'Nema obavijesti.',
    emptyUnread: 'Nema novih obavijesti.',
    settings: 'Postavke obavijesti',
    settingsHelp: 'Za svaku vrstu događaja odaberi želiš li push na uređaj i e-poštu.',
    push: 'Push',
    email: 'E-pošta',
    saved: 'Postavka spremljena.',
    typeWorkoutReminder: 'Podsjetnik na trening',
    typeMissedWorkout: 'Propušten trening',
    typeNewProgram: 'Novi program',
    typeTrainerMessage: 'Poruka trenera',
    typeProgressReminder: 'Podsjetnik na napredak',
    typeMeasurementReminder: 'Podsjetnik na mjerenje',
    typeSubscriptionReminder: 'Podsjetnik na pretplatu',
    typePaymentConfirmation: 'Potvrda plaćanja',
    typePaymentFailed: 'Plaćanje nije prošlo',
    typeWeeklySummary: 'Tjedni sažetak',
    typeReaction: 'Reakcija',
    typeAppointmentScheduled: 'Zakazan termin',
    typeMealLogged: 'Klijent upisao obrok',
  },
  notification: {
    test: 'Probna obavijest',
    newMessage: 'Nova poruka',
    newProgram: 'Dobio/la si novi program',
    mealComment: 'Novi komentar uz obrok',
    checkInSubmitted: 'Klijent je poslao check-in',
    checkInReviewed: 'Trener je odgovorio na tvoj check-in',
    appointmentScheduled: 'Zakazan ti je termin',
    mealLogged: '{name} je upisao/la obrok',
    workoutReminder: 'Podsjetnik na trening',
    weeklySummary: 'Tvoj tjedni sažetak',
    paymentFailed: 'Plaćanje nije prošlo',
  },
  checkin: {
    formLead: 'Sve je neobavezno. Ispuni koliko ti se da — i djelomičan check-in treneru puno govori.',
    stepHow: 'Kako si ovaj tjedan',
    stepNumbers: 'Brojke',
    stepMessage: 'Poruka treneru',
    stepMessageHint: 'Ovo trener čita prvo. Slobodno napiši i ono što ne ide.',
    title: 'Tjedni check-in',
    week: 'Tjedan od {date}',
    weight: 'Težina',
    energy: 'Energija',
    sleep: 'San',
    energyHelp: '1 = iscrpljeno, 5 = puna baterija',
    sleepHelp: '1 = loše, 5 = odlično',
    nutritionAdherence: 'Pridržavanje prehrane',
    trainingAdherence: 'Pridržavanje treninga',
    comment: 'Komentar',
    commentPlaceholder: 'Kako je prošao tjedan? Što je bilo teško?',
    submit: 'Pošalji check-in',
    submitted: 'Check-in poslan.',
    statusOpen: 'Otvoren',
    statusSubmitted: 'Predano — čeka trenera',
    statusReviewed: 'Trener je odgovorio',
    locked: 'Trener je odgovorio na ovaj check-in pa se više ne može mijenjati.',
    trainerResponse: 'Odgovor trenera',
    awaiting: 'Predano. Trener će odgovoriti uskoro.',
    history: 'Prijašnji check-inovi',
    noHistory: 'Još nema predanih check-inova.',
    pendingTitle: 'Check-inovi klijenata',
    noPending: 'Nema check-inova koji čekaju odgovor.',
    respond: 'Odgovori',
    responsePlaceholder: 'Napiši povratnu informaciju klijentu…',
    responseSent: 'Odgovor poslan.',
    responseRequired: 'Odgovor ne smije biti prazan.',
    noResponseYet: 'Trener još nije odgovorio.',
  },
  habits: {
    title: 'Navike',
    streak: 'Niz',
    streakDays: '{n} dana',
    today: 'Danas',
    markToday: 'Označi danas',
    unmarkToday: 'Poništi za danas',
    lastDays: 'Zadnjih {n} dana',
    completion: 'Ispunjeno {n}%',
    create: 'Nova navika',
    name: 'Naziv navike',
    key: 'Ključ',
    keyHint: 'Mala slova, brojke i _ (npr. voda_2l).',
    target: 'Ciljna vrijednost',
    unit: 'Jedinica',
    created: 'Navika dodana.',
    remove: 'Ugasi naviku',
    removeQuestion: 'Ugasiti naviku „{name}”? Povijest niza ostaje sačuvana.',
    removed: 'Navika ugašena.',
    empty: 'Još nemaš navika. Dodaj prvu i niz počinje danas.',
    done: 'Ispunjeno',
    notDone: 'Nije ispunjeno',
    gridLabel: 'Mreža zadnjih {n} dana — {name}',
    invalidKey: 'Ključ smije sadržavati samo mala slova, brojke i _.',
    nameRequired: 'Naziv je obavezan.',
  },
  goals: {
    title: 'Ciljevi',
    create: 'Novi cilj',
    label: 'Naziv',
    type: 'Vrsta',
    typeWeight: 'Težina',
    typeMeasurement: 'Mjera',
    typeWorkoutsPerWeek: 'Treninga tjedno',
    typeSteps: 'Koraci',
    typeStrength: 'Snaga',
    typeCustom: 'Vlastiti',
    start: 'Početno',
    current: 'Trenutno',
    target: 'Cilj',
    due: 'Rok',
    achieved: 'Ostvareno',
    markAchieved: 'Označi ostvarenim',
    achievedOn: 'Ostvareno {date}',
    created: 'Cilj dodan.',
    updated: 'Cilj ažuriran.',
    empty: 'Još nema ciljeva. Postavi prvi i napredak dobiva smjer.',
    progress: 'Napredak',
    noProgress: 'Napredak se računa čim postoji početna vrijednost.',
    targetRequired: 'Ciljna vrijednost je obavezna.',
  },
  cal: {
    title: 'Kalendar',
    prev: 'Prethodni mjesec',
    next: 'Sljedeći mjesec',
    today: 'Danas',
    workout: 'Trening',
    event: 'Termin',
    done: 'Odrađeno',
    missed: 'Propušteno',
    upcoming: 'Predstojeće',
    legend: 'Legenda',
    empty: 'U ovom mjesecu nema ničega u kalendaru.',
    dayEmpty: 'Ništa zakazano.',
    newEvent: 'Zakaži termin',
    client: 'Klijent',
    selectClient: 'Odaberi klijenta',
    selectClientHelp: 'Odaberi klijenta iznad da vidiš njegov kalendar i zakazane termine.',
    eventType: 'Vrsta termina',
    typeAppointment: 'Termin uživo',
    typeCheckIn: 'Check-in',
    typeMeasurement: 'Mjerenje',
    typeRest: 'Odmor',
    eventTitle: 'Naslov',
    startsAt: 'Početak',
    location: 'Lokacija',
    note: 'Bilješka',
    created: 'Termin zakazan.',
    deleteEvent: 'Obriši termin',
    deleteQuestion: 'Obrisati termin „{title}”?',
    deleted: 'Termin obrisan.',
    notEditable: 'Propisane treninge mijenja program, ne kalendar.',
    startRequired: 'Početak termina je obavezan.',
    dayLabel: '{date} — {n} stavki',
    planWorkout: 'Zakaži trening',
    planWorkoutHelp: 'Upiši kad ćeš trenirati i dobit ćeš podsjetnik.',
    planDate: 'Datum',
    planTime: 'Vrijeme',
    planTitle: 'Naziv termina',
    planTitlePlaceholder: 'npr. Trening u teretani',
    linkWorkout: 'Vezan trening iz plana',
    noLinkedWorkout: 'Bez vezanog treninga',
    reminder: 'Podsjetnik',
    reminderNone: 'Bez podsjetnika',
    reminderMinutes: '{n} min prije',
    planned: 'Zakazano',
    plannedByYou: 'Termin koji si sam/a zakazao/la',
    prescribed: 'Propisani raspored',
    reschedule: 'Premjesti',
    rescheduleTitle: 'Premjesti termin',
    planCreated: 'Termin je zakazan.',
    planMoved: 'Termin je premješten.',
    planPast: 'Termin mora biti u budućnosti.',
    dateRequired: 'Odaberi datum i vrijeme.',
    participants: 'Sudionici',
    participantsSearch: 'Traži klijenta',
    participantsSelected: 'Odabrano: {n}',
    participantsAll: 'Označi sve',
    participantsNone: 'Odznači sve',
    participantsEmpty: 'Još nemaš nijednog aktivnog klijenta.',
    participantsNoMatch: 'Nijedan klijent ne odgovara pretrazi.',
    participantsRequired: 'Odaberi barem jednog sudionika.',
    participantsEdit: 'Uredi sudionike',
    participantsSaved: 'Popis sudionika je spremljen.',
    groupCount: '{n} sudionika',
    groupHint: 'Grupna aktivnost — svaki sudionik je vidi u svom kalendaru.',
    groupDeleteQuestion: 'Obrisati „{title}” svim sudionicima ({n})?',
    createdGroup: 'Termin zakazan za {n} sudionika.',
  },
  chart: {
    range1w: '1t',
    range1m: '1m',
    range3m: '3m',
    range6m: '6m',
    range1y: '1g',
    range: 'Raspon',
    metric: 'Metrika',
    metricWeight: 'Težina',
    metricWaist: 'Struk',
    metricChest: 'Prsa',
    metricHips: 'Bokovi',
    metricArm: 'Nadlaktica',
    metricThigh: 'Bedro',
    metricBodyFat: 'Postotak masti',
    metricVolume: 'Volumen',
    metricWorkouts: 'Broj treninga',
    metricDuration: 'Trajanje',
    change: 'Promjena',
    min: 'Najmanje',
    max: 'Najviše',
    average: 'Prosjek',
    noData: 'Za odabrani raspon nema podataka.',
    showTable: 'Prikaži tablicu',
    hideTable: 'Sakrij tablicu',
    day: 'Dan',
    value: 'Vrijednost',
    summaryPeriod: 'Sažetak razdoblja {from} – {to}',
    onePoint: 'Jedna izmjera u razdoblju — graf treba barem dvije.',
  },
  weekly: {
    title: 'Traži pažnju',
    week: 'Tjedan od {date}',
    allGood: 'Nitko ne traži hitnu pažnju. Lijep tjedan.',
    reasonMissed: '{n} propuštenih treninga',
    reasonUnread: '{n} nepročitanih poruka',
    reasonCheckIn: 'Check-in čeka odgovor',
    reasonMeals: 'Prestao/la bilježiti prehranu',
    reasonMeasurements: 'Nema novih mjera',
    reasonAdherence: 'Dosljednost ispod 60 %',
    adherence: 'Dosljednost',
    lastWorkout: 'Zadnji trening',
    workoutsCaption: 'treninga',
    never: 'Nikad',
    others: 'Ostali klijenti',
    empty: 'Još nemaš klijenata u tjednom pregledu.',
  },
  action: {
    create: 'Napravi',
    edit: 'Uredi',
    delete: 'Obriši',
    confirmDelete: 'Da, obriši',
    duplicate: 'Dupliciraj',
    add: 'Dodaj',
    done: 'Gotovo',
    optional: 'Nije obavezno',
  },
  summary: {
    title: 'Tjedni sažetak',
    subtitle: 'Kako je prošao tjedan — bez uljepšavanja.',
    lastWeek: 'Zadnji tjedan',
    earlier: 'Raniji tjedni',
    weekOf: 'Tjedan od {date}',
    workouts: 'Treninzi',
    steps: 'Koraci',
    averageMinutes: 'Prosječno trajanje',
    consistency: 'Dosljednost',
    consistencyValue: '{pct} %',
    empty: 'Još nema tjednog sažetka. Prvi stiže nakon tjedna treninga.',
    highlightAllWorkouts: 'Svi propisani treninzi su odrađeni. Točno onako kako je plan tražio.',
    highlightWeightDown: 'Težina je na kraju tjedna niža nego na početku.',
    highlightSteps: 'Tjedni cilj koraka je ispunjen.',
  },
  achievements: {
    title: 'Postignuća',
    empty: 'Prvo postignuće stiže s prvim odrađenim treningom.',
    unlockedTitle: 'Novo postignuće',
    unlockedHelp: 'Otključano ovim treningom.',
  },
  education: {
    title: 'Edukacija',
    subtitle: 'Tekstovi i materijali koje trener koristi s klijentima.',
    filter: 'Kategorija',
    categoryAll: 'Sve',
    read: 'Pročitaj',
    locked: 'Zaključano',
    seeWhatsInside: 'Pogledaj što je unutra',
    empty: 'U ovoj kategoriji još nema sadržaja.',
    lockedTitle: 'Ovaj sadržaj je dio plaćenog paketa',
    lockedHelp: 'Članak postoji i čeka te — otključava se kupnjom paketa uz koji ide.',
    unlock: 'Pogledaj pakete',
    backToList: 'Natrag na popis',
    readingTime: '{n} min čitanja',
    download: 'Preuzmi materijal',
    noBody: 'Ovaj članak još nema tekst.',
    categoryNutrition: 'Prehrana',
    categoryTraining: 'Trening',
    categoryRecovery: 'Oporavak',
    categoryMobility: 'Mobilnost',
    categoryLifestyle: 'Navike',
  },
  adminContent: {
    title: 'Edukacijski sadržaj',
    subtitle: 'Članci koje klijenti vide u sekciji Edukacija. Skice ostaju skrivene dok ih ne objaviš.',
    create: 'Novi članak',
    edit: 'Uredi članak',
    empty: 'Još nema nijednog članka. Napravi prvi i sekcija Edukacija prestaje biti prazna.',
    created: 'Članak je spremljen kao skica.',
    saved: 'Izmjene su spremljene.',
    deleted: 'Članak je obrisan.',
    published: 'Članak je objavljen.',
    unpublished: 'Članak je povučen s popisa.',
    category: 'Kategorija',
    catNutrition: 'Prehrana',
    catTraining: 'Trening',
    catRecovery: 'Oporavak',
    catMobility: 'Mobilnost',
    catLifestyle: 'Navike',
    titleHr: 'Naslov (HR)',
    titleEn: 'Naslov (EN)',
    slugHr: 'Slug (HR)',
    slugEn: 'Slug (EN)',
    slugHint: 'Završi u adresi članka: mala slova, brojke i crtica.',
    slugEnHint: 'Ostavi prazno ako članak postoji samo na hrvatskom.',
    bodyHr: 'Tekst (HR)',
    bodyEn: 'Tekst (EN)',
    bodyHint: 'Markdown: ## za naslov, - za nabrajanje, **podebljano**.',
    noTranslation: 'Bez prijevoda',
    access: 'Pristup',
    freeForAll: 'Dostupno svima',
    lockedGeneric: 'Uz paket',
    requiresProduct: 'Zaključaj iza paketa',
    requiresProductHint: 'Bez odabira članak vide svi prijavljeni klijenti.',
    status: 'Status',
    statusPublished: 'Objavljeno',
    statusDraft: 'Skica',
    publish: 'Objavi',
    unpublish: 'Povuci',
    deleteTitle: 'Brisanje članka',
    deleteQuestion: 'Obrisati članak „{title}"? Tekst se ne može vratiti — ako ga samo želiš sakriti, povuci objavu.',
  },
  discount: {
    haveCode: 'Imaš kod?',
    label: 'Popust kod',
    placeholder: 'npr. LJETO20',
    apply: 'Primijeni',
    appliedAmount: 'Kod {code} priznat: −{amount}. Za platiti: {total}.',
    appliedPercent: 'Kod {code} priznat: −{value} %.',
    appliedPlain: 'Kod {code} je priznat.',
    rejectUnknown: 'Taj kod ne postoji. Provjeri je li točno prepisan.',
    rejectNotYet: 'Kod još nije počeo vrijediti. Pokušaj kad počne akcija.',
    rejectExpired: 'Kod je istekao.',
    rejectUsedUp: 'Kod je iskorišten do kraja.',
    rejectWrongProduct: 'Ovaj kod ne vrijedi za odabrani paket.',
    rejectAlreadyUsed: 'Taj kod je već iskorišten.',
  },
  discountAdmin: {
    title: 'Popust kodovi',
    create: 'Novi kod',
    created: 'Kod je spremljen.',
    empty: 'Još nema nijednog koda.',
    code: 'Kod',
    codeHint: 'Sprema se velikim slovima.',
    kind: 'Vrsta popusta',
    kindPercent: 'Postotak',
    kindAmount: 'Fiksni iznos',
    percent: 'Postotak',
    amount: 'Iznos',
    value: 'Popust',
    maxRedemptions: 'Najviše iskorištenja',
    maxRedemptionsHint: 'Ostavi prazno za neograničeno.',
    redemptions: 'Iskorišteno',
    unlimitedShort: '{n} / ∞',
    validFrom: 'Vrijedi od',
    validUntil: 'Vrijedi do',
    noExpiry: 'Bez roka',
    product: 'Vezan uz proizvod',
    productHint: 'Bez odabira kod vrijedi za sve proizvode.',
    allProducts: 'Svi proizvodi',
    turnOff: 'Ugasi',
    turnOn: 'Uključi',
  },
  metrics: {
    subtitle: 'Brojke se računaju na serveru — izvještaji i ovaj ekran vide isto.',
    users: 'Korisnici',
    usersTotal: 'Ukupno korisnika',
    active7: 'Aktivni (7 d)',
    active30: 'Aktivni (30 d)',
    revenueTotal: 'Prihod ukupno',
    revenue30: 'Prihod (30 d)',
    subscriptionsActive: 'Aktivne pretplate',
    canceled30: 'Otkazane (30 d)',
    trialsActive: 'Probni pristupi',
    trialsConverted: 'Prešli u plaćeno',
    workoutsTotal: 'Odrađeni treninzi',
    workoutsPerClient: 'Prosjek po klijentu',
    topProducts: 'Najpopularniji proizvodi',
    otherProducts: 'Ostalo',
    noProducts: 'Još nema kupljenih proizvoda.',
    engagementTitle: 'Aktivnost korisnika',
    engagementHelp: 'Aktivni su podskup ukupnih, zato se crtaju na istoj ljestvici.',
    tableLabel: 'Stavka',
    tableValue: 'Vrijednost',
  },
  a11y: {
    progressRing: 'Napredak: {value} od {max}',
    closeDialog: 'Zatvori dijalog',
    loading: 'Sadržaj se učitava',
  },
} as const;

type PortalDict = {
  [K in keyof typeof hr]: { [P in keyof (typeof hr)[K]]: string };
};

const en: PortalDict = {
  shell: {
    trainer: 'Coach',
    admin: 'Admin',
    clients: 'Clients',
    programs: 'Programs',
    exercises: 'Exercises',
    users: 'Users',
    products: 'Products',
    content: 'Content',
    payments: 'Payments',
    analytics: 'Analytics',
    submissions: 'Enquiries',
    checkIn: 'Check-in',
    habits: 'Habits',
    education: 'Learn',
    discountCodes: 'Codes',
    calendar: 'Calendar',
    website: 'Website',
    logout: 'Log out',
    language: 'Language',
    menu: 'Menu',
    skipToContent: 'Skip to content',
  },
  dash: {
    greeting: 'Hi, {name}',
    greetingAnon: 'Hi',
    today: 'Today',
    consistency: 'Consistency',
    consistencyOf: '{done}/{planned} workouts',
    consistencyHelp: 'This week',
    caloriesToday: 'Calories today',
    kcalOf: '{eaten} / {goal} kcal',
    lastMessage: 'Latest message from your coach',
    openMessages: 'Open messages',
    exercisesCount: '{n} exercises',
    minutes: '{n} min',
    volumeWeek: 'Volume this week',
    weight: 'Weight',
    weightGoal: 'Goal {n} kg',
    streakUnit: 'days in a row',
  },
  training: {
    title: 'Training',
    plan: 'Plan',
    history: 'History',
    sets: 'Sets',
    reps: 'Reps',
    weightKg: 'Weight (kg)',
    addedWeightKg: 'Added weight (kg)',
    addWeight: '+ weight',
    durationSeconds: 'Time (s)',
    rest: 'Rest',
    restTimer: 'Rest timer',
    restDone: 'Rest is over.',
    finishNeedsSet: 'Mark at least one set before you can finish the workout.',
    leaveTitle: 'This workout isn’t saved yet',
    leaveQuestion: 'Leaving now discards the sets you entered. Leave anyway?',
    leaveConfirm: 'Leave without saving',
    leaveStay: 'Stay in the workout',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    skip: 'Skip',
    addSet: 'Add set',
    setN: 'Set {n}',
    markDone: 'Mark set',
    finish: 'Finish workout',
    finished: 'Workout complete',
    totalVolume: 'Total volume',
    duration: 'Duration',
    newRecords: 'New personal bests',
    noRecords: 'No new personal bests — every set still counts.',
    backToDashboard: 'Back to home',
    tempo: 'Tempo',
    trainerNote: 'Note from your coach',
    invalidReps: 'Reps must be a whole number between 0 and 1000.',
    invalidWeight: 'Weight must be between 0 and 1000 kg.',
    invalidDuration: 'Time must be between 0 and 28800 seconds.',
    prWeight: 'heaviest weight',
    prVolume: 'best set volume',
    prReps: 'most reps',
  },
  measureGuide: {
    title: 'How to measure',
    help: 'For each point write a sentence and, if you have one, a video. Clients see it when they tap "How to measure" next to that field in the app. A point without a video is fine — the full measuring video is offered instead, and if there is none, just the text.',
    textHr: 'Guide (Croatian)',
    textEn: 'Guide (English)',
    hasVideo: 'Has video',
    noVideo: 'No video',
    upload: 'Upload video',
    replace: 'Replace video',
    remove: 'Remove video',
    uploading: 'Uploading video…',
    uploaded: 'Video uploaded. Save so clients can see it.',
    saved: 'Guide saved.',
    preview: 'Video preview: {point}',
    tooLarge: 'The video is too large (max {max} MB). Record a shorter clip or compress it.',
    wrongType: 'Only MP4 and WebM are allowed.',
  },
  transformacije: {
    title: 'Transformations',
    intro:
      'Before / after pairs for the public site. A pair goes live only once both photos exist and you say so — a new entry never publishes itself.',
    consent:
      'Only publish photos of people who have signed a consent form. If someone agreed to the photo but not the name, settle that before publishing.',
    nova: 'New transformation',
    ime: 'Name',
    imeHint: 'How it will appear under the photo on the site.',
    godine: 'Age',
    mjera: 'Result',
    mjeraHint: 'Minus is a loss, plus a gain. Without a result the card has nothing to claim.',
    jedinicaKg: 'kilograms',
    jedinicaCm: 'centimetres off the waist',
    tekstHr: 'Quote (Croatian)',
    tekstEn: 'Quote (English)',
    prije: 'Before',
    poslije: 'After',
    postavi: 'Upload photo',
    zamijeni: 'Replace',
    saljem: 'Uploading photo…',
    objavljeno: 'Published',
    skica: 'Draft',
    objavi: 'Publish',
    sakrij: 'Hide from the site',
    obrisi: 'Delete',
    obrisiPotvrda: 'Delete this transformation? The photos stay in storage, the record does not.',
    spremi: 'Save',
    spremljeno: 'Saved.',
    prazno: 'No transformations yet.',
    trebaObje: 'Publishing needs both photos — one picture next to an empty frame is not a comparison.',
    prevelika: 'The photo is too large (max {max} MB).',
    krivTip: 'Only JPEG, PNG and WebP are allowed.',
    uzKod:
      'The first 58 transformations live alongside the site code and do not appear in this list. Anything you add here joins them.',
  },
  mealTemplates: {
    title: 'Meal templates',
    intro: 'A menu you build once and your clients pick from with one tap. It removes what holds them back most: working out calories and macros themselves.',
    new: 'New template',
    empty: 'No templates yet.',
    name: 'Name',
    nameHint: 'As the client will see it — "Oats with banana", "Lunch: chicken and rice".',
    nameRequired: 'Give the template a name.',
    slot: 'When',
    slotHint: 'Leave empty if the meal is not tied to a time of day.',
    anySlot: 'Any time',
    note: 'Note',
    noteHint: 'Amount, swaps, what to have with it. The client sees this next to the template.',
    newTitle: 'New meal template',
    editTitle: 'Edit template',
    saved: 'Template saved.',
    deleted: 'Template deleted.',
    deleteTitle: 'Delete template',
    deleteQuestion: 'Delete "{name}"? Meals already logged stay untouched — a template is copied into the meal when picked.',
    pickTitle: 'Your coach\u2019s menu',
    pickHelp: 'Tap and the numbers fill in. You can still change them.',
    pick: 'Log: {meal}',
    fromMeal: 'Save as template',
    fromMealTitle: 'New template from this meal',
  },
  nutrition: {
    photo: 'Meal photo',
    photoHint: 'Snap the plate — it tells your coach far more than a description. Optional.',
    photoPick: 'Choose or take a photo',
    photoPreview: 'Preview of the selected photo',
    photoSavedMealOnly: 'The meal is saved, but the photo did not go through.',
    photoRetry: 'Send the photo again',
    photoSkip: 'Continue without a photo',
    photoTypeInvalid: 'JPEG, PNG, WebP and HEIC are supported.',
    photoTooLarge: 'That photo is too large. Maximum {max} MB.',
    title: 'Nutrition',
    kcal: 'Calories',
    protein: 'Protein',
    carbs: 'Carbs',
    fat: 'Fat',
    meals: 'Meals',
    addMeal: 'Add meal',
    grams: '{n} g',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    water: 'Water',
    trainerComment: 'Coach',
    mealPhoto: 'Meal photo',
    photoOpen: 'Enlarge meal photo',
    formLead: 'Writing what you ate is enough. Everything else is optional.',
    stepWhat: 'What did you eat?',
    stepHowMuch: 'How much',
    stepPhoto: 'Photo',
    required: 'required',
    optional: 'optional',
    quickTitle: 'Quicker',
    howMuchHint: 'Do not know the numbers? Skip them. Your coach sees the meal either way.',
    foodsTitle: 'Ingredients',
    foodsSearch: 'Search ingredients',
    foodsSearchHint: 'chicken, rice, banana…',
    foodsGrams: 'How many grams',
    foodsAdd: 'Add to meal',
    foodsPer100: '{kcal} kcal / 100 g',
    foodsResult: 'Adds {kcal} kcal and {protein} g of protein.',
    slotBreakfast: 'Breakfast',
    slotLunch: 'Lunch',
    slotDinner: 'Dinner',
    slotSnack: 'Snack',
    slot: 'Meal time',
    description: 'What you ate',
    descriptionHint: 'Write what you ate and roughly how much — your coach builds advice on it.',
    descriptionRequired: 'Write what you ate — that is all we need.',
    kcalRange: 'Calories must be a whole number between 0 and {max}.',
    macroRange: 'The value must be between 0 and {max} g.',
    mealSaved: 'Meal saved.',
    mealDeleted: 'Meal deleted.',
    deleteMeal: 'Delete meal',
    deleteMealQuestion: 'This deletes the meal “{meal}”. It cannot be undone.',
    deleteMealOf: 'Delete meal: {meal}',
    history: 'Nutrition history',
    mealsCount: '{n} meals',
  },
  progress: {
    title: 'Progress',
    weightChart: 'Weight over time',
    measurements: 'Measurements',
    photos: 'Photos',
    addMeasurement: 'Add measurement',
    nothingToSave: 'Enter at least one value before saving.',
    addPhoto: 'Add photo',
    before: 'Before',
    now: 'Now',
    waist: 'Waist',
    chest: 'Chest',
    hips: 'Hips',
    arm: 'Upper arm',
    thigh: 'Thigh',
    bodyFat: 'Body fat',
    delta: 'Change',
    charts: 'Charts',
    noChartData: 'Not enough measurements for a chart yet.',
    guide: 'How to measure yourself',
    guideHelp: 'Measure in the morning, on an empty stomach, always in the same spot.',
    guideVideo: 'Measurement video guide',
    guideNoVideo: 'The video guide hasn’t been filmed yet — until it is, the written steps apply.',
    uploadPhoto: 'Upload photo',
    photoFile: 'Photo',
    photoLimits: 'JPEG, PNG, WebP or HEIC, up to {max} MB.',
    photoTypeInvalid: 'That file type is not supported. Pick a JPEG, PNG, WebP or HEIC.',
    photoTooLarge: 'The photo is larger than {max} MB. Pick a smaller one or resize it first.',
    photoRequired: 'Pick a photo before uploading.',
    photoPreview: 'Preview of the selected photo',
    photoNote: 'Photo note',
    photoUploaded: 'Photo saved.',
    photoUnavailable: 'This photo is unavailable right now.',
    photoPrivacy: 'Progress photos are private. Your coach sees them only if you explicitly grant access.',
    takenOn: 'Date taken',
    pose: 'Pose',
    poseFront: 'Front',
    poseSide: 'Side',
    poseBack: 'Back',
    poseOther: 'Other',
    stageGrant: 'Preparing the upload…',
    stageUpload: 'Uploading the photo…',
    stageConfirm: 'Saving the record…',
    retryFromConfirm: 'The photo is already uploaded — retrying only finishes saving it.',
  },
  messages: {
    title: 'Messages',
    placeholder: 'Write a message…',
    send: 'Send',
  },
  profile: {
    title: 'Profile',
    personal: 'Personal details',
    preferences: 'Preferences',
    units: 'Units',
    metric: 'Metric (kg, cm)',
    imperial: 'Imperial (lb, in)',
    timezone: 'Time zone',
    changePassword: 'Change password',
    saved: 'Saved.',
  },
  install: {
    title: 'App on your phone',
    body: 'Add Soul Training to your home screen and it opens like an app — no address bar, its own icon next to everything else. It works the same until we ship on Google Play.',
    action: 'Add to home screen',
    installed: 'The app is already added on this device.',
    iosTitle: 'On iPhone and iPad',
    iosSteps: 'Open Safari, tap the share icon and choose "Add to Home Screen".',
    manualTitle: 'In this browser',
    manualSteps: 'Open the browser menu and choose "Install app" or "Add to Home screen".',
    done: 'The app was added to your home screen.',
  },
  webPush: {
    title: 'Browser notifications',
    body: 'While the browser is running, notifications arrive even when the portal is closed — a message from a client, a new meal entry, a booked session.',
    switch: 'Notifications on this device',
    on: 'On. They arrive even when the portal is closed.',
    off: 'Off. You will only see notifications in the bell, while the portal is open.',
    test: 'Send a test',
    blocked: 'The browser has blocked notifications for this site.',
    blockedHelp: 'A switch here cannot undo that — open the site settings (the icon left of the address), allow notifications and reload the page.',
    unsupported: 'This browser does not support notifications.',
    notReady: 'Notifications work on the published portal, not in local development.',
    notConfigured: 'Browser notifications are not set up on the server yet.',
    enabled: 'Notifications are on.',
    moved: 'Notifications in this browser now go to you. The account that had them here no longer gets them — each browser holds only one subscription.',
    disabled: 'Notifications are off.',
    failed: 'Turning them on failed. Try again.',
  },
  subscription: {
    title: 'Subscription',
    currentPlan: 'Current plan',
    status: 'Status',
    nextPayment: 'Next payment',
    noNextPayment: 'No upcoming payment',
    managePortal: 'Manage subscription',
    managePortalHelp: 'Card, invoices and cancellation live in the Stripe Customer Portal.',
    accessUntil: 'Access until {date}',
    statusActive: 'Active',
    statusPastDue: 'Payment failed',
    statusCanceled: 'Cancelled',
    statusExpired: 'Expired',
    invoices: 'Invoices',
    choosePlan: 'Choose a package',
    plans: 'Packages',
    plansHelp: 'A purchase unlocks the whole program, with no cap on the number of workouts.',
    buy: 'Get access',
    checkoutFailed: 'Checkout could not be started.',
  },
  onboarding: {
    title: 'Let’s get to know you',
    step: 'Step {n} of {total}',
    finish: 'Finish',
    done: 'Thanks! Your coach now has everything they need.',
    goal: 'What is your main goal?',
    activity: 'How active are you?',
    body: 'Basic measurements',
    height: 'Height (cm)',
    startWeight: 'Current weight (kg)',
    experience: 'Training experience',
    equipment: 'Equipment you have',
    notes: 'Injuries, limitations, anything I should know',
    goalLoss: 'Fat loss',
    goalTone: 'Toning',
    goalStrength: 'Strength and muscle',
    goalHealth: 'Energy and health',
    actSedentary: 'Sedentary',
    actModerate: 'Moderate',
    actActive: 'Active',
    actVeryActive: 'Very active',
    expNone: 'No experience',
    expSome: 'I train occasionally',
    expRegular: 'I train regularly',
    eqNone: 'No equipment',
    eqHome: 'Home equipment',
    eqGym: 'Gym',
  },
  wizard: {
    title: 'Let’s get to know you',
    stepOf: 'Step {n} of {total}',
    basics: 'Basic details',
    goal: 'Your goal',
    program: 'Your program',
    enterHint: 'Press Enter to continue',
    firstName: 'What is your first name?',
    firstNameLabel: 'First name',
    lastName: 'And your last name?',
    lastNameLabel: 'Last name',
    birthDate: 'When were you born?',
    birthDateLabel: 'Date of birth',
    birthDateHint: 'Your age helps us set the right training load.',
    gender: 'Gender',
    genderLabel: 'Gender',
    genderFemale: 'Female',
    genderMale: 'Male',
    genderOther: 'Other',
    genderUndisclosed: 'Prefer not to say',
    heightCm: 'How tall are you?',
    heightLabel: 'Height',
    weightKg: 'What do you weigh right now?',
    weightLabel: 'Weight',
    weightHint: 'This is also your first measurement — the weight chart starts here.',
    required: 'This field is required.',
    invalidHeight: 'Height must be between 100 and 250 cm.',
    invalidWeight: 'Weight must be between 30 and 300 kg.',
    invalidDate: 'Pick your date of birth.',
    goalQuestion: 'What do you want to achieve?',
    goalHelp: 'Pick one. You can change it later.',
    targetWeight: 'Target weight',
    targetWeightHint: 'What weight are you aiming for?',
    targetWeightRequired: 'For a weight-loss goal, enter your target weight.',
    programQuestion: 'Pick a program to start with',
    programHelp: 'Start right away — the first workouts are free and no card is needed.',
    trialBadgeOne: 'first workout free',
    trialBadgeFew: 'first {n} workouts free',
    trialBadgeMany: 'first {n} workouts free',
    chooseProgram: 'Choose this program',
    noPrograms: 'No programs available right now. Get in touch with your coach.',
    done: 'Everything’s ready. Let’s go.',
  },
  trial: {
    title: 'Free trial',
    remainingOne: '{n} free workout left',
    remainingFew: '{n} free workouts left',
    remainingMany: '{n} free workouts left',
    bannerHelp: 'After that the rest of the program unlocks with a purchase.',
    used: '{used} of {total} used',
    lockedTitle: 'This workout unlocks with a purchase',
    lockedHelp: 'The first workouts of the program are free. Pick a package and the whole program opens up.',
    sectionTitle: 'A plan unlocks this',
    sectionHelp:
      'Nutrition, measurements, messages and the weekly check-in are part of coaching. Choose a plan and it all opens up.',
    lockedBadge: 'Locked',
    unlock: 'Unlock with a purchase',
    lockedFrom: 'Locked from workout: {name}',
    locked: 'Locked workout',
  },
  trainer: {
    title: 'Coach',
    clients: 'Clients',
    clientProfile: 'Client profile',
    tabOverview: 'Overview',
    tabWorkouts: 'Workouts',
    flagMissed: '{n} missed',
    flagNoMeasurements: 'No measurements',
    flagUnread: 'Unread message',
    lastActive: 'Last active',
    consistency: 'Consistency',
    assignedProgram: 'Assigned program',
    weeks: '{n} weeks',
    newProgram: 'New program',
    newExercise: 'New exercise',
    muscleGroup: 'Muscle group',
    equipment: 'Equipment',
    checkIns: 'Check-ins',
    messages: 'Messages',
    all: 'All',
    clientSummaries: 'Client weekly summaries',
    clientSummariesHelp: 'The same summary the client receives on Monday.',
    clientSummariesEmpty:
      'No weekly summary for this client yet. The first one arrives on Monday after a week of training.',
    clientNutrition: 'Nutrition',
    clientNutritionHelp: 'Meals the client logs and photographs. Leave a thumbs-up or a comment — the client sees it on the meal.',
    clientNutritionEmpty: 'No meals logged for that day.',
    nutritionDayPick: 'Day with entries',
    mealLike: 'Thumbs up',
    mealLikeOf: 'Mark “{meal}” as fine',
    mealUnlikeOf: 'Remove the mark from “{meal}”',
    mealComment: 'Comment',
    mealCommentEdit: 'Edit comment',
    mealCommentLabel: 'Comment on “{meal}”',
    clientAchievements: 'Client achievements',
    clientAchievementsEmpty: 'This client hasn’t unlocked any achievements yet.',
  },
  admin: {
    title: 'Admin',
    users: 'Users',
    products: 'Products and prices',
    content: 'Content',
    payments: 'Payments',
    analytics: 'Analytics',
    role: 'Role',
    createdAt: 'Registered',
    lastSeen: 'Last seen',
    deleted: 'Deleted',
    access: 'Access',
    accessActive: 'Active',
    accessNone: 'None',
    grantAccess: 'Grant access',
    revokeAccess: 'Revoke access',
    grantTitle: 'Manual access grant',
    grantHelp:
      'For payments outside Stripe — bank transfer or cash. The client becomes the trainer’s and the app unlocks for them.',
    grantProduct: 'Plan',
    grantUntil: 'Valid until',
    grantUntilHelp: 'Leave empty for no end date.',
    grantNote: 'Note',
    grantNoteHelp: 'Stored permanently. E.g. “Bank transfer 4 Sep, €150”.',
    revokeTitle: 'Revoke access',
    revokeHelp: 'Ends every manually granted right for this client. Stripe subscriptions are untouched.',
    price: 'Price',
    interval: 'Interval',
    compareAt: 'Compare at',
    active: 'Active',
    inactive: 'Inactive',
    amount: 'Amount',
    method: 'Method',
    refund: 'Refund',
    paid: 'Paid',
    failed: 'Failed',
    refunded: 'Refunded',
    pending: 'Pending',
    submissions: 'Enquiries',
    conversion: 'Conversion',
    activeUsers: 'Active users (30d)',
    churn: 'Client churn',
    churnHint: 'Share of subscriptions cancelled in the last 30 days. Lower is better.',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Log in to your portal.',
    registerTitle: 'Create your account',
    registerSubtitle: 'Your account exists before you pay — that way nothing gets lost.',
    forgotTitle: 'Forgot password',
    forgotSubtitle: 'We’ll email you a link to set a new password.',
    forgotSubmit: 'Send link',
    forgotSent: 'If the account exists, a link is on its way.',
    resetTitle: 'New password',
    resetSubmit: 'Set password',
    resetDone: 'Password changed. Log in with the new one.',
    verifyTitle: 'Email confirmation',
    verifyPending: 'Checking the link…',
    verifyDone: 'Email confirmed. You can log in now.',
    verifyFailed: 'This link is invalid or has expired.',
    verifyResend: 'Send a new link',
    noAccount: 'No account yet?',
    hasAccount: 'Already have an account?',
    acceptTerms: 'I accept the terms of use and the privacy policy.',
    termsRequired: 'We can’t open an account until you accept the terms.',
    passwordHint: 'At least 10 characters.',
    passwordMismatch: 'The passwords don’t match.',
    emailTaken: 'An account with that address already exists.',
    backToLogin: 'Back to login',
    missingToken: 'The link has no token.',
  },
  empty: {
    noWorkoutsPlanned: 'No workouts prescribed.',
    noExercises: 'The exercise library is empty.',
    noProducts: 'No products.',
    noPayments: 'No payments recorded.',
    noUsers: 'No users.',
    noInvoices: 'No invoices.',
    noSubscription: 'You have no active subscription.',
    noSubmissions: 'No enquiries yet. Contact-form messages and assessment requests land here.',
    addFirstMeasurement: 'Add measurement',
    addFirstMeal: 'Add meal',
    addFirstPhoto: 'Add photo',
    browsePlans: 'See packages',
    noMealsPortal: 'No meals logged for this day.',
    noHistory: 'Your nutrition history is empty. The first meal you log shows up here.',
    onePhotoOnly: 'You have one progress photo. The comparison appears once there are at least two.',
    noProgramChosen: 'You haven’t picked a program yet. Pick one and the first workouts open right away.',
    chooseProgram: 'Choose a program',
  },
  error: {
    rateLimited: 'Too many attempts.',
    rateLimitedHelp: 'Wait a minute and try again.',
    validation: 'Check the details you entered.',
    validationHelp: 'Some fields aren’t filled in correctly.',
    conflict: 'This data has changed since you opened it.',
    conflictHelp: 'Refresh the page and try again.',
  },
  builder: {
    title: 'Program builder',
    backToPrograms: 'All programs',
    openBuilder: 'Open',
    newProgramTitle: 'New program',
    editProgram: 'Edit program',
    deleteProgram: 'Delete program',
    deleteProgramConfirm: 'Delete the program “{name}”? Clients who already have it keep their plan.',
    programName: 'Program name',
    programNameEn: 'Name in English',
    programDescription: 'Description',
    programLevel: 'Level (1–5)',
    isTemplate: 'Template',
    isTemplateHelp: 'A template can be assigned to many clients.',
    nameRequired: 'The program name is required.',
    weeksShort: '{n} wks',
    created: 'Program created.',
    deleted: 'Program deleted.',
    saved: 'Saved.',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    week: 'Week {n}',
    addWeek: 'Add week',
    duplicateWeek: 'Duplicate week',
    deleteWeek: 'Delete week',
    deleteWeekConfirm: 'Delete {week} with all its workouts? The remaining weeks are renumbered.',
    weekDuplicated: 'Week duplicated.',
    emptyProgram: 'This program has no weeks yet. Add the first one and lay out the workouts.',
    addWorkout: 'Add workout',
    newWorkout: 'New workout',
    editWorkout: 'Edit workout',
    duplicateWorkout: 'Duplicate workout',
    deleteWorkout: 'Delete workout',
    deleteWorkoutConfirm: 'Delete the workout “{name}” and all of its exercises?',
    workoutName: 'Workout name',
    workoutType: 'Workout type',
    estimatedMinutes: 'Estimated duration (min)',
    workoutNote: 'Workout note',
    day: 'Day',
    typeStrength: 'Strength',
    typeCardio: 'Cardio',
    typeMobility: 'Mobility',
    typeRest: 'Rest',
    typeMixed: 'Mixed',
    exercises: 'Exercises',
    exerciseCount: '{n} exercises',
    noExercises: 'This workout has no exercises yet.',
    addExercise: 'Add exercise',
    searchExercise: 'Search exercises by name',
    allCategories: 'All categories',
    noSearchResults: 'No exercise matches this search.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeExercise: 'Remove exercise',
    reorderHint: 'Drag an exercise or use the arrows to reorder.',
    reorderFailed: 'The new order wasn’t saved.',
    setsLabel: 'Sets',
    repsLabel: 'Reps',
    repsHint: 'Free text: 8–12, to failure…',
    restLabel: 'Rest (s)',
    tempoLabel: 'Tempo',
    weightHint: 'Suggested weight (kg)',
    noteLabel: 'Note for the client',
    prescribedSets: 'Prescribed sets',
    prescribedSetsHelp: 'Pyramid or drop set, set by set. Leave empty to use the prescription above.',
    addPrescribedSet: 'Add prescribed set',
    removePrescribedSet: 'Remove prescribed set',
    setWeight: 'Weight (kg)',
    setNote: 'Set note',
    saveExercise: 'Save exercise',
    exerciseSaved: 'Exercise saved.',
    dayTaken: 'That day already has a workout.',
    weekFull: 'The week is full — no free day left.',
    assign: 'Assign to client',
    assignTitle: 'Assign program',
    assignClient: 'Client',
    chooseClient: 'Choose a client',
    assignStartDate: 'Start date',
    assignReplace: 'Replace the client’s active program',
    assignReplaceHelp: 'The previous assignment is closed; the history is kept.',
    assignSubmit: 'Assign program',
    assignDone: 'Program assigned to the client.',
    clientRequired: 'Choose a client.',
    startDateRequired: 'Choose a start date.',
  },
  exerciseLib: {
    newTitle: 'New exercise',
    editTitle: 'Edit exercise',
    slug: 'Slug',
    slugHint: 'Lowercase letters, digits and dashes only.',
    slugInvalid: 'A slug may only contain lowercase letters, digits and dashes.',
    slugRequired: 'The slug is required.',
    nameHr: 'Name (Croatian)',
    nameEn: 'Name (English)',
    nameRequired: 'Both names are required.',
    category: 'Category',
    muscleGroups: 'Muscle groups',
    muscleGroupsHint: 'Separate with commas.',
    equipment: 'Equipment',
    loadType: 'How it is measured',
    loadTypeHint: 'Decides which fields the client sees during the workout.',
    loadWeighted: 'Reps and weight',
    loadBodyweight: 'Bodyweight (reps only)',
    loadTimed: 'Hold (time)',
    difficulty: 'Difficulty',
    difficultyBeginner: 'Beginner',
    difficultyIntermediate: 'Intermediate',
    difficultyAdvanced: 'Advanced',
    instructions: 'Instructions',
    globalBadge: 'Global',
    edit: 'Edit',
    catChest: 'Chest',
    catBack: 'Back',
    catShoulders: 'Shoulders',
    catArms: 'Arms',
    catLegs: 'Legs',
    catGlutes: 'Glutes',
    catCore: 'Core',
    catCardio: 'Cardio',
    catMobility: 'Mobility',
    catFullBody: 'Full body',
  },
  product: {
    newTitle: 'New product',
    editTitle: 'Edit product',
    edit: 'Edit',
    slug: 'Slug',
    slugRequired: 'The slug is required.',
    nameHr: 'Name (Croatian)',
    nameEn: 'Name (English)',
    nameHrRequired: 'The Croatian name is required.',
    nameEnRequired: 'The English name is required.',
    descriptionHr: 'Description (Croatian)',
    descriptionEn: 'Description (English)',
    featuresHr: 'Features (Croatian)',
    featuresEn: 'Features (English)',
    featuresHelp: 'One feature per line.',
    accessDurationDays: 'Access duration (days)',
    accessDurationHelp: 'Leave empty when access lasts as long as the subscription.',
    accessDurationValue: '{n} days of access',
    deliveryMode: 'Delivery mode',
    modeOnline: 'Online',
    modeGym: 'In person',
    modeHybrid: 'Hybrid',
    highlighted: 'Highlighted on the pricing page',
    activeLabel: 'Active for sale',
    sortOrder: 'Sort order',
    noPrices: 'This package has no price yet, so it cannot be bought.',
    saved: 'Product saved.',
    trialWorkouts: 'Free workouts',
    trialWorkoutsHelp: 'How many workouts a client does before payment is asked for. 0 = none.',
    program: 'Training plan',
    programHelp: 'What the client gets on purchase. Without a plan the package has no program.',
    programNone: 'No plan',
    sortOrderHelp: 'A lower number sits closer to the top of the pricing page.',
  },
  price: {
    newTitle: 'New price',
    editTitle: 'Edit price',
    replaceTitle: 'Change the amount',
    add: 'Add price',
    edit: 'Edit',
    replace: 'Change amount',
    amount: 'Amount',
    amountHelp: 'In euros. Stored in cents.',
    currency: 'Currency',
    compareAt: 'Compare-at price',
    compareAtHelp: 'Shown struck through next to the amount. Does not affect billing.',
    interval: 'Billing period',
    intervalCount: 'Every',
    intervalCountHelp: 'A quarterly price is month × 3.',
    labelHr: 'Label (HR)',
    labelEn: 'Label (EN)',
    labelHelp: 'Sits next to the amount on the pricing page, e.g. “per quarter”.',
    activeLabel: 'Show on the pricing page',
    amountRequired: 'Enter an amount',
    saved: 'Price saved.',
    replaced: 'The new price is saved and the old one is switched off.',
    inStripe: 'On sale',
    notInStripe: 'Not on sale',
    lockedHelp:
      'This price already exists in Stripe, so the amount cannot be edited — Stripe prices are immutable. “Change amount” creates a new price and switches this one off.',
    replaceHelp:
      'The old price is switched off and leaves the pricing page, but stays in the database because existing purchases point at it. The new one goes on sale after syncing.',
  },
  stripe: {
    title: 'Stripe',
    pendingNone: 'Every price is on sale.',
    pending: 'Waiting to sync: {products} packages and {prices} prices.',
    sync: 'Sync',
    synced: 'Synced: {products} packages, {prices} prices.',
    help:
      'Syncing creates the package and price in Stripe. Until a price is there it shows on the pricing page but cannot be bought.',
  },
  refund: {
    action: 'Refund',
    title: 'Confirm refund',
    question: 'Refund {amount} to {email}?',
    warning: 'A refund cannot be undone. Stripe processes it immediately and the webhook confirms the state.',
    reason: 'Reason (internal note)',
    confirm: 'Start refund',
    done: 'Refund started.',
    onlySucceeded: 'Only successful payments can be refunded.',
  },
  submissions: {
    title: 'Enquiries',
    subtitle: 'Contact form and free assessment.',
    source: 'Source',
    sourceContactForm: 'Contact form',
    sourceAssessment: 'Free assessment',
    sourceOther: 'Other',
    status: 'Status',
    statusNew: 'New',
    statusContacted: 'Contacted',
    statusConverted: 'Converted',
    statusClosed: 'Closed',
    changeStatus: 'Change status',
    statusSaved: 'Status changed.',
    message: 'Message',
    noMessage: 'This enquiry has no message.',
    assessment: 'Calculator answers',
    received: 'Received',
    filterAll: 'All',
    internalNote: 'Internal note',
    fieldGoal: 'Goal',
    fieldActivity: 'Activity',
    fieldHeightCm: 'Height',
    fieldWeightKg: 'Weight',
    fieldAge: 'Age',
    fieldRecommendation: 'Recommendation',
  },
  chat: {
    conversations: 'Conversations',
    selectConversation: 'Pick a conversation from the list.',
    backToList: 'Back to conversations',
    unread: '{n} unread',
    noConversations: 'No conversations yet.',
    noMessages: 'No messages in this conversation yet.',
    lastMessageNone: 'No messages',
    attachmentPreview: 'Attachment',
    youPrefix: 'You: {text}',
    placeholder: 'Write a message…',
    send: 'Send',
    sending: 'Sending…',
    sendFailed: 'The message wasn’t sent.',
    live: 'Live',
    reconnecting: 'Reconnecting…',
    polling: 'Live connection unavailable — refreshing every 15 s.',
    typing: '{name} is typing…',
    online: 'Online',
    read: 'Read',
    newConversation: 'New conversation',
    startWith: 'Pick a client',
    started: 'Conversation opened.',
  },
  notif: {
    title: 'Notifications',
    open: 'Open notifications',
    unreadCount: '{n} unread notifications',
    filterAll: 'All',
    filterUnread: 'New',
    markRead: 'Mark as read',
    markUnread: 'Mark as unread',
    markAllRead: 'Mark all',
    empty: 'No notifications.',
    emptyUnread: 'No new notifications.',
    settings: 'Notification settings',
    settingsHelp: 'For each kind of event choose whether you want a push and an email.',
    push: 'Push',
    email: 'Email',
    saved: 'Setting saved.',
    typeWorkoutReminder: 'Workout reminder',
    typeMissedWorkout: 'Missed workout',
    typeNewProgram: 'New program',
    typeTrainerMessage: 'Coach message',
    typeProgressReminder: 'Progress reminder',
    typeMeasurementReminder: 'Measurement reminder',
    typeSubscriptionReminder: 'Subscription reminder',
    typePaymentConfirmation: 'Payment confirmation',
    typePaymentFailed: 'Payment failed',
    typeWeeklySummary: 'Weekly summary',
    typeReaction: 'Reaction',
    typeAppointmentScheduled: 'Scheduled session',
    typeMealLogged: 'Client logged a meal',
  },
  notification: {
    test: 'Test notification',
    newMessage: 'New message',
    newProgram: 'You have a new program',
    mealComment: 'New comment on a meal',
    checkInSubmitted: 'A client submitted a check-in',
    checkInReviewed: 'Your coach replied to your check-in',
    appointmentScheduled: 'An appointment was scheduled for you',
    mealLogged: '{name} logged a meal',
    workoutReminder: 'Workout reminder',
    weeklySummary: 'Your weekly summary',
    paymentFailed: 'Payment failed',
  },
  checkin: {
    formLead: 'All optional. Fill in as much as you feel like — even a partial check-in tells your coach a lot.',
    stepHow: 'How was your week',
    stepNumbers: 'Numbers',
    stepMessage: 'Message to your coach',
    stepMessageHint: 'This is what your coach reads first. Write what is not working too.',
    title: 'Weekly check-in',
    week: 'Week of {date}',
    weight: 'Weight',
    energy: 'Energy',
    sleep: 'Sleep',
    energyHelp: '1 = drained, 5 = fully charged',
    sleepHelp: '1 = poor, 5 = great',
    nutritionAdherence: 'Nutrition adherence',
    trainingAdherence: 'Training adherence',
    comment: 'Comment',
    commentPlaceholder: 'How did the week go? What was hard?',
    submit: 'Send check-in',
    submitted: 'Check-in sent.',
    statusOpen: 'Open',
    statusSubmitted: 'Submitted — waiting for your coach',
    statusReviewed: 'Your coach replied',
    locked: 'Your coach replied to this check-in, so it can no longer be changed.',
    trainerResponse: 'Coach reply',
    awaiting: 'Submitted. Your coach will reply soon.',
    history: 'Previous check-ins',
    noHistory: 'No submitted check-ins yet.',
    pendingTitle: 'Client check-ins',
    noPending: 'No check-ins waiting for a reply.',
    respond: 'Reply',
    responsePlaceholder: 'Write feedback for your client…',
    responseSent: 'Reply sent.',
    responseRequired: 'The reply cannot be empty.',
    noResponseYet: 'Your coach hasn’t replied yet.',
  },
  habits: {
    title: 'Habits',
    streak: 'Streak',
    streakDays: '{n} days',
    today: 'Today',
    markToday: 'Mark today',
    unmarkToday: 'Undo today',
    lastDays: 'Last {n} days',
    completion: '{n}% completed',
    create: 'New habit',
    name: 'Habit name',
    key: 'Key',
    keyHint: 'Lowercase letters, digits and _ (e.g. water_2l).',
    target: 'Target value',
    unit: 'Unit',
    created: 'Habit added.',
    remove: 'Turn habit off',
    removeQuestion: 'Turn off the habit “{name}”? The streak history is kept.',
    removed: 'Habit turned off.',
    empty: 'No habits yet. Add the first one and the streak starts today.',
    done: 'Done',
    notDone: 'Not done',
    gridLabel: 'Grid of the last {n} days — {name}',
    invalidKey: 'The key may only contain lowercase letters, digits and _.',
    nameRequired: 'A name is required.',
  },
  goals: {
    title: 'Goals',
    create: 'New goal',
    label: 'Name',
    type: 'Type',
    typeWeight: 'Weight',
    typeMeasurement: 'Measurement',
    typeWorkoutsPerWeek: 'Workouts per week',
    typeSteps: 'Steps',
    typeStrength: 'Strength',
    typeCustom: 'Custom',
    start: 'Start',
    current: 'Current',
    target: 'Target',
    due: 'Due',
    achieved: 'Achieved',
    markAchieved: 'Mark as achieved',
    achievedOn: 'Achieved {date}',
    created: 'Goal added.',
    updated: 'Goal updated.',
    empty: 'No goals yet. Set the first one and progress gets a direction.',
    progress: 'Progress',
    noProgress: 'Progress is calculated as soon as there’s a starting value.',
    targetRequired: 'A target value is required.',
  },
  cal: {
    title: 'Calendar',
    prev: 'Previous month',
    next: 'Next month',
    today: 'Today',
    workout: 'Workout',
    event: 'Appointment',
    done: 'Done',
    missed: 'Missed',
    upcoming: 'Upcoming',
    legend: 'Legend',
    empty: 'Nothing in the calendar this month.',
    dayEmpty: 'Nothing scheduled.',
    newEvent: 'Schedule appointment',
    client: 'Client',
    selectClient: 'Pick a client',
    selectClientHelp: 'Pick a client above to see their calendar and scheduled sessions.',
    eventType: 'Appointment type',
    typeAppointment: 'In-person session',
    typeCheckIn: 'Check-in',
    typeMeasurement: 'Measurement',
    typeRest: 'Rest',
    eventTitle: 'Title',
    startsAt: 'Starts at',
    location: 'Location',
    note: 'Note',
    created: 'Appointment scheduled.',
    deleteEvent: 'Delete appointment',
    deleteQuestion: 'Delete the appointment “{title}”?',
    deleted: 'Appointment deleted.',
    notEditable: 'Prescribed workouts are changed in the program, not the calendar.',
    startRequired: 'A start time is required.',
    dayLabel: '{date} — {n} items',
    planWorkout: 'Schedule a workout',
    planWorkoutHelp: 'Write down when you’ll train and you’ll get a reminder.',
    planDate: 'Date',
    planTime: 'Time',
    planTitle: 'Title',
    planTitlePlaceholder: 'e.g. Gym session',
    linkWorkout: 'Linked workout from the plan',
    noLinkedWorkout: 'No linked workout',
    reminder: 'Reminder',
    reminderNone: 'No reminder',
    reminderMinutes: '{n} min before',
    planned: 'Scheduled',
    plannedByYou: 'A session you scheduled yourself',
    prescribed: 'Prescribed schedule',
    reschedule: 'Move',
    rescheduleTitle: 'Move the session',
    planCreated: 'Session scheduled.',
    planMoved: 'Session moved.',
    planPast: 'The session must be in the future.',
    dateRequired: 'Pick a date and a time.',
    participants: 'Participants',
    participantsSearch: 'Search clients',
    participantsSelected: 'Selected: {n}',
    participantsAll: 'Select all',
    participantsNone: 'Clear all',
    participantsEmpty: 'You do not have any active clients yet.',
    participantsNoMatch: 'No client matches that search.',
    participantsRequired: 'Pick at least one participant.',
    participantsEdit: 'Edit participants',
    participantsSaved: 'Participant list saved.',
    groupCount: '{n} participants',
    groupHint: 'Group activity — every participant sees it in their own calendar.',
    groupDeleteQuestion: 'Delete "{title}" for all {n} participants?',
    createdGroup: 'Scheduled for {n} participants.',
  },
  chart: {
    range1w: '1w',
    range1m: '1m',
    range3m: '3m',
    range6m: '6m',
    range1y: '1y',
    range: 'Range',
    metric: 'Metric',
    metricWeight: 'Weight',
    metricWaist: 'Waist',
    metricChest: 'Chest',
    metricHips: 'Hips',
    metricArm: 'Upper arm',
    metricThigh: 'Thigh',
    metricBodyFat: 'Body fat',
    metricVolume: 'Volume',
    metricWorkouts: 'Workouts',
    metricDuration: 'Duration',
    change: 'Change',
    min: 'Lowest',
    max: 'Highest',
    average: 'Average',
    noData: 'No data for the selected range.',
    showTable: 'Show table',
    hideTable: 'Hide table',
    day: 'Day',
    value: 'Value',
    summaryPeriod: 'Summary for {from} – {to}',
    onePoint: 'Only one reading in this period — a chart needs at least two.',
  },
  weekly: {
    title: 'Needs attention',
    week: 'Week of {date}',
    allGood: 'Nobody needs urgent attention. Good week.',
    reasonMissed: '{n} missed workouts',
    reasonUnread: '{n} unread messages',
    reasonCheckIn: 'Check-in waiting for a reply',
    reasonMeals: 'Stopped logging meals',
    reasonMeasurements: 'No recent measurements',
    reasonAdherence: 'Adherence below 60%',
    adherence: 'Adherence',
    lastWorkout: 'Last workout',
    workoutsCaption: 'workouts',
    never: 'Never',
    others: 'Other clients',
    empty: 'No clients in the weekly overview yet.',
  },
  action: {
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    confirmDelete: 'Yes, delete',
    duplicate: 'Duplicate',
    add: 'Add',
    done: 'Done',
    optional: 'Optional',
  },
  summary: {
    title: 'Weekly summary',
    subtitle: 'How the week actually went — no sugar-coating.',
    lastWeek: 'Last week',
    earlier: 'Earlier weeks',
    weekOf: 'Week of {date}',
    workouts: 'Workouts',
    steps: 'Steps',
    averageMinutes: 'Average duration',
    consistency: 'Consistency',
    consistencyValue: '{pct}%',
    empty: 'No weekly summary yet. The first one arrives after a week of training.',
    highlightAllWorkouts: 'You did every prescribed workout — exactly as planned.',
    highlightWeightDown: 'Your weight ended the week lower than it started.',
    highlightSteps: 'You hit the weekly step goal.',
  },
  achievements: {
    title: 'Achievements',
    empty: 'Your first achievement arrives with your first completed workout.',
    unlockedTitle: 'New achievement',
    unlockedHelp: 'Unlocked by this workout.',
  },
  education: {
    title: 'Learn',
    subtitle: 'Articles and materials your coach uses with clients.',
    filter: 'Category',
    categoryAll: 'All',
    read: 'Read',
    locked: 'Locked',
    seeWhatsInside: 'See what’s inside',
    empty: 'Nothing in this category yet.',
    lockedTitle: 'This content is part of a paid package',
    lockedHelp: 'The article is here waiting — it unlocks with the package it belongs to.',
    unlock: 'See packages',
    backToList: 'Back to the list',
    readingTime: '{n} min read',
    download: 'Download material',
    noBody: 'This article has no text yet.',
    categoryNutrition: 'Nutrition',
    categoryTraining: 'Training',
    categoryRecovery: 'Recovery',
    categoryMobility: 'Mobility',
    categoryLifestyle: 'Lifestyle',
  },
  adminContent: {
    title: 'Learning content',
    subtitle: 'Articles clients see under Learn. Drafts stay hidden until you publish them.',
    create: 'New article',
    edit: 'Edit article',
    empty: 'No articles yet. Create the first one and the Learn section stops being empty.',
    created: 'Article saved as a draft.',
    saved: 'Changes saved.',
    deleted: 'Article deleted.',
    published: 'Article published.',
    unpublished: 'Article pulled from the list.',
    category: 'Category',
    catNutrition: 'Nutrition',
    catTraining: 'Training',
    catRecovery: 'Recovery',
    catMobility: 'Mobility',
    catLifestyle: 'Lifestyle',
    titleHr: 'Title (HR)',
    titleEn: 'Title (EN)',
    slugHr: 'Slug (HR)',
    slugEn: 'Slug (EN)',
    slugHint: 'Ends up in the article URL: lowercase letters, digits and hyphens.',
    slugEnHint: 'Leave empty if the article exists in Croatian only.',
    bodyHr: 'Body (HR)',
    bodyEn: 'Body (EN)',
    bodyHint: 'Markdown: ## for a heading, - for a list, **bold**.',
    noTranslation: 'No translation',
    access: 'Access',
    freeForAll: 'Open to everyone',
    lockedGeneric: 'With a package',
    requiresProduct: 'Lock behind a package',
    requiresProductHint: 'With nothing selected, every signed-in client can read it.',
    status: 'Status',
    statusPublished: 'Published',
    statusDraft: 'Draft',
    publish: 'Publish',
    unpublish: 'Unpublish',
    deleteTitle: 'Delete article',
    deleteQuestion: 'Delete the article “{title}”? The text cannot be restored — to just hide it, unpublish instead.',
  },
  discount: {
    haveCode: 'Have a code?',
    label: 'Discount code',
    placeholder: 'e.g. SUMMER20',
    apply: 'Apply',
    appliedAmount: 'Code {code} applied: −{amount}. To pay: {total}.',
    appliedPercent: 'Code {code} applied: −{value}%.',
    appliedPlain: 'Code {code} applied.',
    rejectUnknown: 'That code doesn’t exist. Check the spelling.',
    rejectNotYet: 'This code is not valid yet. Try again when the offer starts.',
    rejectExpired: 'This code has expired.',
    rejectUsedUp: 'This code has been fully redeemed.',
    rejectAlreadyUsed: 'You have already used this code.',
    rejectWrongProduct: 'This code doesn’t apply to the selected package.',
  },
  discountAdmin: {
    title: 'Discount codes',
    create: 'New code',
    created: 'Code saved.',
    empty: 'No codes yet.',
    code: 'Code',
    codeHint: 'Stored in uppercase.',
    kind: 'Discount type',
    kindPercent: 'Percentage',
    kindAmount: 'Fixed amount',
    percent: 'Percentage',
    amount: 'Amount',
    value: 'Discount',
    maxRedemptions: 'Max redemptions',
    maxRedemptionsHint: 'Leave empty for unlimited.',
    redemptions: 'Redeemed',
    unlimitedShort: '{n} / ∞',
    validFrom: 'Valid from',
    validUntil: 'Valid until',
    noExpiry: 'No expiry',
    product: 'Tied to product',
    productHint: 'With nothing selected, the code applies to every product.',
    allProducts: 'All products',
    turnOff: 'Turn off',
    turnOn: 'Turn on',
  },
  metrics: {
    subtitle: 'Numbers are calculated on the server — the reports and this screen always agree.',
    users: 'Users',
    usersTotal: 'Total users',
    active7: 'Active (7d)',
    active30: 'Active (30d)',
    revenueTotal: 'Revenue total',
    revenue30: 'Revenue (30d)',
    subscriptionsActive: 'Active subscriptions',
    canceled30: 'Cancelled (30d)',
    trialsActive: 'Trials',
    trialsConverted: 'Converted to paid',
    workoutsTotal: 'Workouts completed',
    workoutsPerClient: 'Average per client',
    topProducts: 'Top products',
    otherProducts: 'Other',
    noProducts: 'No products purchased yet.',
    engagementTitle: 'User activity',
    engagementHelp: 'Active users are a subset of the total, so they share one scale.',
    tableLabel: 'Item',
    tableValue: 'Value',
  },
  a11y: {
    progressRing: 'Progress: {value} of {max}',
    closeDialog: 'Close dialog',
    loading: 'Loading content',
  },
};

export const portalDict: Record<Locale, PortalDict> = { hr, en };
export type { PortalDict };
